import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/apiRouting';
import './TableChartManager.css';

// ─── Types ───────────────────────────────────────────────────────────────────

type TableStatus = 'available' | 'occupied' | 'reserved' | 'dirty' | 'cleaning' | 'blocked' | 'closing_soon' | 'merged';
type TableShape = 'square' | 'circle' | 'rectangle' | 'oval' | 'booth' | 'bar';
type EditorMode = 'edit' | 'live' | 'section';
type EditTool = 'select' | 'wall' | 'fixture';
type GridMode = 'visible' | 'snap' | 'hidden';
type WallStyle = 'solid' | 'half-wall' | 'glass' | 'railing';
type FixtureType = 'host-stand' | 'wait-station' | 'pos-terminal' | 'bar-counter' | 'kitchen-door' | 'restroom-m' | 'restroom-f' | 'fireplace' | 'planter' | 'stairs';

interface Wall {
  wallId: string;
  points: { x: number; y: number }[];
  thickness: number;
  color: string;
  style: WallStyle;
  closed: boolean;
}

interface Fixture {
  fixtureId: string;
  type: FixtureType;
  position: { x: number; y: number };
  dimensions: { width: number; height: number };
  rotation: number;
  label: string;
}

interface Section {
  sectionId: string;
  name: string;
  color: string;
  serverName: string;
  tableIds: string[];
}

interface SectionTemplate {
  templateId: string;
  name: string;
  sections: Section[];
  createdAt: string;
}

interface ContextMenuState {
  x: number;
  y: number;
  tableId: string | null;
}

interface Table {
  tableId: string;
  restaurantId: string;
  tableNumber: string;
  displayName?: string;
  tableType: string;
  position: { x: number; y: number };
  dimensions: { width: number; height: number };
  capacity: { default: number; maximum: number; current: number };
  minCovers?: number;
  maxCovers?: number;
  areaId?: string;
  shape: TableShape;
  rotation: number;
  isJoined: boolean;
  joinedWith: string[];
  status: TableStatus;
  active: boolean;
  description?: string;
  tags?: string[];
  notes?: string;
  colorOverride?: string;
  revenueCenter?: string;
  statusChangedAt?: string;
  partySize?: number;
  guestName?: string;
  serverName?: string;
  sectionId?: string;
  spaceId?: string;
}

interface Area {
  areaId: string;
  restaurantId: string;
  name: string;
  color: string;
  description: string;
  isDefault: boolean;
  active: boolean;
}

interface TableType {
  type: string;
  defaultCapacity: number;
  maxCapacity: number;
  width: number;
  height: number;
  shape: TableShape;
  displayName: string;
  category?: string;
}

interface Layout {
  layoutId: string;
  restaurantId: string;
  name: string;
  description: string;
  isDefault: boolean;
  canvasSize: { width: number; height: number };
  backgroundColor: string;
  gridEnabled: boolean;
  gridSize: number;
  snapToGrid: boolean;
  active: boolean;
}

interface TableChartManagerProps {
  restaurantId: string;
  onClose?: () => void;
}

interface StatusConfig {
  color: string;
  border: string;
  label: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<TableStatus, StatusConfig> = {
  available:    { color: '#d1fae5', border: '#10b981', label: 'Available' },
  occupied:     { color: '#dbeafe', border: '#3b82f6', label: 'Occupied' },
  reserved:     { color: '#ede9fe', border: '#8b5cf6', label: 'Reserved' },
  dirty:        { color: '#fee2e2', border: '#ef4444', label: 'Dirty' },
  cleaning:     { color: '#fef3c7', border: '#f59e0b', label: 'Cleaning' },
  blocked:      { color: '#f3f4f6', border: '#6b7280', label: 'Blocked' },
  closing_soon: { color: '#fef3c7', border: '#d97706', label: 'Closing' },
  merged:       { color: '#ccfbf1', border: '#14b8a6', label: 'Merged' },
};

const ALL_STATUSES = Object.keys(STATUS_CONFIG) as TableStatus[];

const DEFAULT_TABLE_LIBRARY: TableType[] = [
  // Square
  { type: 'square-2',  displayName: '2-Top Square',   defaultCapacity: 2, maxCapacity: 2, width: 60, height: 60, shape: 'square', category: 'Square' },
  { type: 'square-4',  displayName: '4-Top Square',   defaultCapacity: 4, maxCapacity: 4, width: 80, height: 80, shape: 'square', category: 'Square' },
  // Round
  { type: 'round-2',   displayName: '2-Top Round',    defaultCapacity: 2, maxCapacity: 2, width: 60, height: 60, shape: 'circle', category: 'Round' },
  { type: 'round-4',   displayName: '4-Top Round',    defaultCapacity: 4, maxCapacity: 4, width: 80, height: 80, shape: 'circle', category: 'Round' },
  { type: 'round-6',   displayName: '6-Top Round',    defaultCapacity: 6, maxCapacity: 8, width: 100, height: 100, shape: 'circle', category: 'Round' },
  { type: 'round-8',   displayName: '8-Top Round',    defaultCapacity: 8, maxCapacity: 10, width: 120, height: 120, shape: 'circle', category: 'Round' },
  // Rectangle
  { type: 'rect-4',    displayName: '4-Top Rect',     defaultCapacity: 4, maxCapacity: 6, width: 120, height: 70, shape: 'rectangle', category: 'Rectangle' },
  { type: 'rect-6',    displayName: '6-Top Rect',     defaultCapacity: 6, maxCapacity: 8, width: 140, height: 70, shape: 'rectangle', category: 'Rectangle' },
  { type: 'rect-8',    displayName: '8-Top Rect',     defaultCapacity: 8, maxCapacity: 10, width: 180, height: 80, shape: 'rectangle', category: 'Rectangle' },
  // Oval
  { type: 'oval-4',    displayName: '4-Top Oval',     defaultCapacity: 4, maxCapacity: 6, width: 120, height: 70, shape: 'oval', category: 'Oval' },
  { type: 'oval-6',    displayName: '6-Top Oval',     defaultCapacity: 6, maxCapacity: 8, width: 140, height: 80, shape: 'oval', category: 'Oval' },
  // Booth
  { type: 'booth-2',   displayName: '2-Top Booth',    defaultCapacity: 2, maxCapacity: 3, width: 80, height: 60, shape: 'booth', category: 'Booth' },
  { type: 'booth-4',   displayName: '4-Top Booth',    defaultCapacity: 4, maxCapacity: 6, width: 120, height: 70, shape: 'booth', category: 'Booth' },
  { type: 'booth-6',   displayName: '6-Top Booth',    defaultCapacity: 6, maxCapacity: 8, width: 160, height: 80, shape: 'booth', category: 'Booth' },
  // High-Top
  { type: 'hightop-2', displayName: '2-Top High-Top', defaultCapacity: 2, maxCapacity: 2, width: 50, height: 50, shape: 'square', category: 'High-Top' },
  { type: 'hightop-4', displayName: '4-Top High-Top', defaultCapacity: 4, maxCapacity: 4, width: 70, height: 70, shape: 'square', category: 'High-Top' },
  // Bar
  { type: 'bar-1',     displayName: 'Bar Seat',       defaultCapacity: 1, maxCapacity: 1, width: 40, height: 30, shape: 'bar', category: 'Bar' },
  { type: 'bar-2',     displayName: 'Bar (2)',        defaultCapacity: 2, maxCapacity: 2, width: 80, height: 30, shape: 'bar', category: 'Bar' },
  // Communal
  { type: 'communal-10', displayName: 'Communal (10)', defaultCapacity: 10, maxCapacity: 14, width: 240, height: 80, shape: 'rectangle', category: 'Communal' },
  { type: 'communal-12', displayName: 'Communal (12)', defaultCapacity: 12, maxCapacity: 16, width: 280, height: 80, shape: 'rectangle', category: 'Communal' },
];

// SVG fixture icons
const FixtureIcons: Record<string, React.ReactNode> = {
  'restroom-m': (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="4" r="2.5"/>
      <path d="M12 7v5m0 0l-3 7m3-7l3 7m-7-10h8"/>
    </svg>
  ),
  'restroom-f': (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="4" r="2.5"/>
      <path d="M9 7h6l-1.5 7H15l-3 6-3-6h1.5L9 7z"/>
    </svg>
  ),
  'stairs': (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 20h4v-4h4v-4h4v-4h4"/>
    </svg>
  ),
  'pos-terminal': (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="2" width="16" height="14" rx="2"/>
      <line x1="4" y1="12" x2="20" y2="12"/>
      <rect x="8" y="18" width="8" height="3" rx="1"/>
      <line x1="12" y1="16" x2="12" y2="18"/>
    </svg>
  ),
  'host-stand': (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="4" width="12" height="16" rx="1"/>
      <line x1="6" y1="10" x2="18" y2="10"/>
      <line x1="10" y1="7" x2="14" y2="7"/>
    </svg>
  ),
  'wait-station': (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="6" width="18" height="14" rx="1"/>
      <line x1="3" y1="12" x2="21" y2="12"/>
      <line x1="12" y1="6" x2="12" y2="20"/>
    </svg>
  ),
  'bar-counter': (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="8" width="20" height="4" rx="1"/>
      <line x1="5" y1="12" x2="5" y2="20"/>
      <line x1="19" y1="12" x2="19" y2="20"/>
    </svg>
  ),
  'kitchen-door': (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="2" width="18" height="20" rx="1"/>
      <line x1="12" y1="2" x2="12" y2="22"/>
      <circle cx="9" cy="12" r="1" fill="currentColor"/>
      <circle cx="15" cy="12" r="1" fill="currentColor"/>
    </svg>
  ),
  'fireplace': (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="6" width="20" height="14" rx="2"/>
      <path d="M12 18c-2 0-3-2-3-4 0-3 3-5 3-7 0 2 3 4 3 7 0 2-1 4-3 4z"/>
    </svg>
  ),
  'planter': (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 16h10l1 5H6l1-5z"/>
      <path d="M12 16c0-4-4-6-4-9a4 4 0 018 0c0 3-4 5-4 9z"/>
    </svg>
  ),
};

const FIXTURE_LIBRARY: { type: FixtureType; label: string; width: number; height: number; icon: React.ReactNode }[] = [
  { type: 'host-stand', label: 'Host Stand', width: 60, height: 40, icon: FixtureIcons['host-stand'] },
  { type: 'wait-station', label: 'Wait Station', width: 50, height: 50, icon: FixtureIcons['wait-station'] },
  { type: 'pos-terminal', label: 'POS Terminal', width: 40, height: 40, icon: FixtureIcons['pos-terminal'] },
  { type: 'bar-counter', label: 'Bar Counter', width: 200, height: 40, icon: FixtureIcons['bar-counter'] },
  { type: 'kitchen-door', label: 'Kitchen Door', width: 60, height: 10, icon: FixtureIcons['kitchen-door'] },
  { type: 'restroom-m', label: 'Restroom (M)', width: 50, height: 50, icon: FixtureIcons['restroom-m'] },
  { type: 'restroom-f', label: 'Restroom (F)', width: 50, height: 50, icon: FixtureIcons['restroom-f'] },
  { type: 'fireplace', label: 'Fireplace', width: 80, height: 30, icon: FixtureIcons['fireplace'] },
  { type: 'planter', label: 'Planter', width: 40, height: 40, icon: FixtureIcons['planter'] },
  { type: 'stairs', label: 'Stairs', width: 60, height: 80, icon: FixtureIcons['stairs'] },
];

const SECTION_COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

const BACKGROUND_PRESETS = [
  { name: 'White', color: '#ffffff' },
  { name: 'Light Gray', color: '#f8f9fa' },
  { name: 'Warm Beige', color: '#f5f0e8' },
  { name: 'Oak', color: '#deb887' },
  { name: 'Walnut', color: '#5c4033' },
  { name: 'Slate', color: '#708090' },
  { name: 'Charcoal', color: '#36454f' },
  { name: 'Black', color: '#1a1a2e' },
];

const GRID_SIZE = 20;
const MAX_UNDO = 20;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function normalizeTable(t: any, restaurantId: string): Table {
  const validShapes: TableShape[] = ['square', 'circle', 'rectangle', 'oval', 'booth', 'bar'];
  const validStatuses: TableStatus[] = ALL_STATUSES;
  let shape: TableShape = 'square';
  if (validShapes.includes(t.shape)) shape = t.shape;
  let status: TableStatus = 'available';
  if (validStatuses.includes(t.status)) status = t.status;

  // Support both nested (position: {x,y}) and flat (x, y) DB formats
  const posX = t.position?.x ?? t.x ?? 100;
  const posY = t.position?.y ?? t.y ?? 100;

  // Support both nested capacity and flat seats field
  const seats = Number(t.seats) || 4;
  const defaultCap = t.capacity?.default ?? seats;
  const maxCap = t.capacity?.maximum ?? seats;
  const currentCap = t.capacity?.current ?? 0;

  return {
    tableId: t.tableId || `t-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    restaurantId: t.restaurantId || restaurantId,
    tableNumber: t.tableNumber || '0',
    displayName: t.displayName || '',
    tableType: t.tableType || 'dining',
    position: { x: Number(posX), y: Number(posY) },
    dimensions: { width: t.dimensions?.width ?? 80, height: t.dimensions?.height ?? 80 },
    capacity: {
      default: defaultCap,
      maximum: maxCap,
      current: currentCap,
    },
    minCovers: t.minCovers ?? 0,
    maxCovers: t.maxCovers ?? maxCap,
    areaId: t.areaId || t.section || undefined,
    shape,
    rotation: t.rotation ?? 0,
    isJoined: t.isJoined ?? false,
    joinedWith: t.joinedWith || [],
    status,
    active: t.active !== false,
    description: t.description || '',
    tags: t.tags || [],
    notes: t.notes || '',
    colorOverride: t.colorOverride || undefined,
    revenueCenter: t.revenueCenter || '',
    statusChangedAt: t.statusChangedAt || t.seatedAt || undefined,
    partySize: t.partySize ?? undefined,
    guestName: t.guestName || '',
    serverName: t.serverName || '',
    spaceId: t.spaceId || undefined,
  };
}

function snapToGrid(val: number, gridSize: number): number {
  return Math.round(val / gridSize) * gridSize;
}

function formatTimer(isoDate: string | undefined): string {
  if (!isoDate) return '';
  const diff = Date.now() - new Date(isoDate).getTime();
  if (diff < 0 || isNaN(diff)) return '';
  const mins = Math.floor(diff / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function getNextTableNumber(tables: Table[]): string {
  const nums = tables.map(t => parseInt(t.tableNumber, 10)).filter(n => !isNaN(n));
  return nums.length === 0 ? '1' : String(Math.max(...nums) + 1);
}

function computeAlignGuides(draggedTable: Table, allTables: Table[], threshold: number = 5): { type: 'h' | 'v'; pos: number }[] {
  const guides: { type: 'h' | 'v'; pos: number }[] = [];
  const dx = draggedTable.position.x;
  const dy = draggedTable.position.y;
  const dw = draggedTable.dimensions.width;
  const dh = draggedTable.dimensions.height;
  const dCx = dx + dw / 2;
  const dCy = dy + dh / 2;

  for (const t of allTables) {
    if (t.tableId === draggedTable.tableId) continue;
    const tx = t.position.x, ty = t.position.y;
    const tw = t.dimensions.width, th = t.dimensions.height;
    const tCx = tx + tw / 2, tCy = ty + th / 2;
    // Vertical guides (x alignment)
    if (Math.abs(dx - tx) < threshold) guides.push({ type: 'v', pos: tx });
    if (Math.abs(dx + dw - tx - tw) < threshold) guides.push({ type: 'v', pos: tx + tw });
    if (Math.abs(dCx - tCx) < threshold) guides.push({ type: 'v', pos: tCx });
    // Horizontal guides (y alignment)
    if (Math.abs(dy - ty) < threshold) guides.push({ type: 'h', pos: ty });
    if (Math.abs(dy + dh - ty - th) < threshold) guides.push({ type: 'h', pos: ty + th });
    if (Math.abs(dCy - tCy) < threshold) guides.push({ type: 'h', pos: tCy });
  }
  // Deduplicate
  const seen = new Set<string>();
  return guides.filter(g => {
    const key = `${g.type}_${g.pos}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function snapWallAngle(start: { x: number; y: number }, end: { x: number; y: number }): { x: number; y: number } {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const angle = Math.atan2(dy, dx) * (180 / Math.PI);
  const snapAngles = [0, 45, 90, 135, 180, -45, -90, -135, -180];
  let closest = 0;
  let minDiff = 999;
  for (const sa of snapAngles) {
    const diff = Math.abs(angle - sa);
    if (diff < minDiff) { minDiff = diff; closest = sa; }
  }
  if (minDiff < 10) {
    const dist = Math.sqrt(dx * dx + dy * dy);
    const rad = closest * (Math.PI / 180);
    return { x: start.x + Math.cos(rad) * dist, y: start.y + Math.sin(rad) * dist };
  }
  return end;
}

// ─── Component ───────────────────────────────────────────────────────────────

const TableChartManager: React.FC<TableChartManagerProps> = ({ restaurantId, onClose }) => {
  const { getIdToken } = useAuth();

  // Authenticated fetch helper
  const authFetch = useCallback(async (url: string, options: RequestInit = {}): Promise<Response> => {
    const token = await getIdToken();
    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string> || {}),
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    if (options.method && options.method !== 'GET' && options.method !== 'DELETE') {
      headers['Content-Type'] = headers['Content-Type'] || 'application/json';
    }
    return fetch(url, { ...options, headers });
  }, [getIdToken]);

  // Core state
  const [tables, setTables] = useState<Table[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [layouts, setLayouts] = useState<Layout[]>([]);
  const [currentLayout, setCurrentLayout] = useState<Layout | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Mode
  const [mode, setMode] = useState<EditorMode>('edit');
  const [editTool, setEditTool] = useState<EditTool>('select');
  const [gridMode, setGridMode] = useState<GridMode>('visible');

  // Selection (multi-select)
  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
  const [selectedTableIds, setSelectedTableIds] = useState<Set<string>>(new Set());
  const [selectedArea, setSelectedArea] = useState<string | null>(null);

  // Selection rectangle
  const [selectionRect, setSelectionRect] = useState<{ startX: number; startY: number; endX: number; endY: number } | null>(null);
  const [isDrawingSelection, setIsDrawingSelection] = useState(false);

  // Context menu
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);

  // Resize handles
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState<string | null>(null);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, w: 0, h: 0 });

  // Walls & Fixtures
  const [walls, setWalls] = useState<Wall[]>([]);
  const [fixtures, setFixtures] = useState<Fixture[]>([]);
  const [wallDrawPoints, setWallDrawPoints] = useState<{ x: number; y: number }[]>([]);
  const [wallPreviewPoint, setWallPreviewPoint] = useState<{ x: number; y: number } | null>(null);
  const [wallThickness, setWallThickness] = useState(6);
  const [wallColor, setWallColor] = useState('#374151');
  const [wallStyle, setWallStyle] = useState<WallStyle>('solid');

  // Sections
  const [sections, setSections] = useState<Section[]>([]);
  const [sectionTemplates, setSectionTemplates] = useState<SectionTemplate[]>([]);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);

  // Background
  const [bgColor, setBgColor] = useState('#f8f9fa');
  const [bgPattern, setBgPattern] = useState<string | null>(null);

  // Smart alignment guides
  const [alignGuides, setAlignGuides] = useState<{ type: 'h' | 'v'; pos: number }[]>([]);

  // Minimap
  const [showMinimap, setShowMinimap] = useState(true);

  // Save state
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const dirtyTableIds = useRef<Set<string>>(new Set());

  // Fixture/Wall selection & drag
  const [selectedFixtureId, setSelectedFixtureId] = useState<string | null>(null);
  const [selectedWallId, setSelectedWallId] = useState<string | null>(null);
  const [isDraggingFixture, setIsDraggingFixture] = useState(false);
  const [fixtureOffset, setFixtureOffset] = useState({ x: 0, y: 0 });

  // Canvas / zoom / pan
  const [scale, setScale] = useState(0.75);
  const [pan, setPan] = useState({ x: 20, y: 20 });
  const [canvasSize, setCanvasSize] = useState({ width: 1200, height: 800 });
  const canvasWrapperRef = useRef<HTMLDivElement>(null);

  // Drag state
  const [isDraggingTable, setIsDraggingTable] = useState(false);
  const [isDraggingFromLibrary, setIsDraggingFromLibrary] = useState(false);
  const [dragLibraryItem, setDragLibraryItem] = useState<TableType | null>(null);
  const [dragGhostPos, setDragGhostPos] = useState({ x: 0, y: 0 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [showDropIndicator, setShowDropIndicator] = useState(false);

  // Undo/Redo
  const [undoStack, setUndoStack] = useState<Table[][]>([]);
  const [redoStack, setRedoStack] = useState<Table[][]>([]);

  // Modals
  const [showPropertiesModal, setShowPropertiesModal] = useState(false);
  const [modalTable, setModalTable] = useState<Table | null>(null);

  // Live mode status dropdown
  const [statusDropdown, setStatusDropdown] = useState<{ tableId: string; x: number; y: number } | null>(null);

  // Timer tick
  const [, setTick] = useState(0);

  // Library
  const [tableLibrary, setTableLibrary] = useState<TableType[]>(DEFAULT_TABLE_LIBRARY);
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());

  // New tag input
  const [newTag, setNewTag] = useState('');

  const selectedTable = tables.find(t => t.tableId === selectedTableId) || null;
  const multiSelectedTables = tables.filter(t => selectedTableIds.has(t.tableId));

  // ─── Data Loading ────────────────────────────────────────────────────────

  useEffect(() => {
    loadData();
  }, [restaurantId]);

  // Timer tick for live mode
  useEffect(() => {
    if (mode !== 'live') return;
    const interval = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(interval);
  }, [mode]);

  // Mark dirty when anything changes (skip initial load)
  const initialLoadDone = useRef(false);
  useEffect(() => {
    if (loading) return;
    if (!initialLoadDone.current) { initialLoadDone.current = true; return; }
    setIsDirty(true);
  }, [tables, walls, fixtures, sections, bgColor, bgPattern]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      await Promise.all([
        loadTables(), loadAreas(), loadLayouts(), loadTableLibrary(),
        loadWalls(), loadFixtures(), loadFloorConfig(), loadSectionTemplates(),
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadTables = async () => {
    try {
      const url = API_ENDPOINTS.restaurantTables(restaurantId);
      const response = await authFetch(url);
      if (!response.ok) {
        console.warn('Table Charts API not available:', response.status);
        setTables([]);
        return;
      }
      const data = await response.json();
      const list = data.tables || data.data || [];
      const valid = list.filter((t: any) => t && t.tableId);
      setTables(valid.map((t: any) => normalizeTable(t, restaurantId)));
    } catch (err) {
      console.warn('Table Charts API error:', err);
      setTables([]);
    }
  };

  const loadAreas = async () => {
    try {
      const response = await authFetch(API_ENDPOINTS.restaurantAreas(restaurantId));
      if (!response.ok) { setAreas([]); return; }
      const data = await response.json();
      setAreas(data.areas || data.data || []);
    } catch {
      setAreas([]);
    }
  };

  const createSpaceAPI = async (name: string): Promise<Layout | null> => {
    try {
      const res = await authFetch(API_ENDPOINTS.restaurantLayouts(restaurantId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          description: '',
          isDefault: false,
          canvasSize: { width: 1200, height: 800 },
          backgroundColor: '#f8f9fa',
          gridEnabled: true,
          gridSize: 20,
          snapToGrid: true,
          active: true,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        return {
          layoutId: data.layoutId,
          restaurantId,
          name: data.name || name,
          description: data.description || '',
          isDefault: data.isDefault || false,
          canvasSize: data.canvasSize || { width: 1200, height: 800 },
          backgroundColor: data.backgroundColor || '#f8f9fa',
          gridEnabled: data.gridEnabled !== false,
          gridSize: data.gridSize || 20,
          snapToGrid: data.snapToGrid !== false,
          active: true,
        };
      }
    } catch (err) {
      console.warn('Failed to create space:', err);
    }
    return null;
  };

  const loadLayouts = async () => {
    try {
      const response = await authFetch(API_ENDPOINTS.restaurantLayouts(restaurantId));
      if (!response.ok) { applyDefaultSpace(); return; }
      const data = await response.json();
      const list = (data.layouts || data.data || []).filter(
        (l: any) => l.layoutId && l.layoutId.startsWith('layout_')
      ).map((l: any) => ({
        layoutId: l.layoutId,
        restaurantId: l.restaurantId || restaurantId,
        name: l.name || 'Untitled Space',
        description: l.description || '',
        isDefault: l.isDefault || false,
        canvasSize: l.canvasSize || { width: 1200, height: 800 },
        backgroundColor: l.backgroundColor || '#f8f9fa',
        gridEnabled: l.gridEnabled !== false,
        gridSize: l.gridSize || 20,
        snapToGrid: l.snapToGrid !== false,
        active: l.active !== false,
      }));
      if (list.length > 0) {
        setLayouts(list);
        if (!currentLayout) setCurrentLayout(list[0]);
      } else {
        applyDefaultSpace();
      }
    } catch {
      applyDefaultSpace();
    }
  };

  const applyDefaultSpace = async () => {
    const created = await createSpaceAPI('Main Floor');
    if (created) {
      setLayouts([created]);
      if (!currentLayout) setCurrentLayout(created);
    } else {
      // Fallback if API fails - use local-only default
      const fallback: Layout = {
        layoutId: 'default', restaurantId, name: 'Main Floor', description: '',
        isDefault: true, canvasSize: { width: 1200, height: 800 }, backgroundColor: '#f8f9fa',
        gridEnabled: true, gridSize: 20, snapToGrid: true, active: true,
      };
      setLayouts([fallback]);
      if (!currentLayout) setCurrentLayout(fallback);
    }
  };

  const loadTableLibrary = async () => {
    try {
      const response = await authFetch(API_ENDPOINTS.tableLibrary());
      if (!response.ok) return;
      const data = await response.json();
      const apiTables = data.tables || data.data;
      if (apiTables && typeof apiTables === 'object') {
        const arr: TableType[] = Array.isArray(apiTables) ? apiTables : Object.values(apiTables);
        if (arr.length > 0) setTableLibrary(arr.map(t => ({ ...t, category: t.category || 'Other' })));
      }
    } catch {
      // keep default library
    }
  };

  const loadWalls = async () => {
    try {
      const response = await authFetch(API_ENDPOINTS.restaurantWalls(restaurantId));
      if (!response.ok) return;
      const data = await response.json();
      const list = data.walls || [];
      setWalls(list.map((w: any) => ({
        wallId: w.layoutId || w.wallId,
        points: w.points || [],
        thickness: w.thickness || 6,
        color: w.color || '#374151',
        style: w.style || 'solid',
        closed: w.closed || false,
      })));
    } catch {
      // no walls saved yet
    }
  };

  const loadFixtures = async () => {
    try {
      const response = await authFetch(API_ENDPOINTS.restaurantFixtures(restaurantId));
      if (!response.ok) return;
      const data = await response.json();
      const list = data.fixtures || [];
      setFixtures(list.map((f: any) => ({
        fixtureId: f.layoutId || f.fixtureId,
        type: f.type || 'host-stand',
        position: f.position || { x: 0, y: 0 },
        dimensions: f.dimensions || { width: 60, height: 40 },
        rotation: f.rotation || 0,
        label: f.label || '',
      })));
    } catch {
      // no fixtures saved yet
    }
  };

  const loadFloorConfig = async () => {
    try {
      const response = await authFetch(API_ENDPOINTS.floorConfig(restaurantId));
      if (!response.ok) return;
      const data = await response.json();
      if (data.bgColor) setBgColor(data.bgColor);
      if (data.bgPattern) setBgPattern(data.bgPattern);
      if (data.sections) setSections(data.sections);
    } catch {
      // no config saved yet
    }
  };

  const loadSectionTemplates = async () => {
    try {
      const response = await authFetch(API_ENDPOINTS.sectionTemplates(restaurantId));
      if (!response.ok) return;
      const data = await response.json();
      const list = data.templates || [];
      setSectionTemplates(list.map((t: any) => ({
        templateId: t.layoutId || t.templateId,
        name: t.name || 'Untitled',
        sections: t.sections || [],
        createdAt: t.createdAt || '',
      })));
    } catch {
      // no templates saved yet
    }
  };

  // ─── Undo / Redo ────────────────────────────────────────────────────────

  const pushUndo = useCallback(() => {
    setUndoStack(prev => {
      const next = [...prev, tables.map(t => ({ ...t }))];
      if (next.length > MAX_UNDO) next.shift();
      return next;
    });
    setRedoStack([]);
  }, [tables]);

  const undo = useCallback(() => {
    setUndoStack(prev => {
      if (prev.length === 0) return prev;
      const next = [...prev];
      const snapshot = next.pop()!;
      setRedoStack(r => [...r, tables.map(t => ({ ...t }))]);
      setTables(snapshot);
      snapshot.forEach(t => dirtyTableIds.current.add(t.tableId));
      return next;
    });
  }, [tables]);

  const redo = useCallback(() => {
    setRedoStack(prev => {
      if (prev.length === 0) return prev;
      const next = [...prev];
      const snapshot = next.pop()!;
      setUndoStack(u => [...u, tables.map(t => ({ ...t }))]);
      setTables(snapshot);
      snapshot.forEach(t => dirtyTableIds.current.add(t.tableId));
      return next;
    });
  }, [tables]);

  // ─── API Mutations ──────────────────────────────────────────────────────

  const saveTable = async (table: Table) => {
    const url = API_ENDPOINTS.restaurantTable(restaurantId, table.tableId);
    const res = await authFetch(url, {
      method: 'PUT',
      body: JSON.stringify(table),
    });
    if (!res.ok) {
      const errText = await res.text().catch(() => '');
      console.error('Failed to save table:', res.status, errText);
      throw new Error(`Save failed (${res.status})`);
    }
  };

  const createTableAPI = async (table: Table): Promise<Table> => {
    try {
      const url = API_ENDPOINTS.restaurantTables(restaurantId);
      const res = await authFetch(url, {
        method: 'POST',
        body: JSON.stringify(table),
      });
      if (res.ok) {
        const data = await res.json();
        return normalizeTable(data.data || data.table || data, restaurantId);
      }
    } catch (err) {
      console.warn('Failed to create table via API:', err);
    }
    return table;
  };

  const deleteTableAPI = async (tableId: string) => {
    try {
      const url = API_ENDPOINTS.restaurantTable(restaurantId, tableId);
      await authFetch(url, { method: 'DELETE' });
    } catch (err) {
      console.warn('Failed to delete table:', err);
    }
  };

  const updateTableStatusAPI = async (tableId: string, status: TableStatus) => {
    try {
      const url = API_ENDPOINTS.updateTableStatus(restaurantId, tableId);
      await authFetch(url, {
        method: 'PUT',
        body: JSON.stringify({ status, statusChangedAt: new Date().toISOString() }),
      });
    } catch (err) {
      console.warn('Failed to update status:', err);
    }
  };

  // ─── Wall / Fixture / Config API Mutations ─────────────────────────────

  const createWallAPI = async (wall: Wall) => {
    try {
      await authFetch(API_ENDPOINTS.restaurantWalls(restaurantId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ points: wall.points, thickness: wall.thickness, color: wall.color, style: wall.style, closed: wall.closed }),
      });
    } catch (err) { console.warn('Failed to save wall:', err); }
  };

  const deleteWallAPI = async (wallId: string) => {
    try {
      await authFetch(API_ENDPOINTS.restaurantWall(restaurantId, wallId), { method: 'DELETE' });
    } catch (err) { console.warn('Failed to delete wall:', err); }
  };

  const createFixtureAPI = async (fixture: Fixture) => {
    try {
      await authFetch(API_ENDPOINTS.restaurantFixtures(restaurantId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: fixture.type, position: fixture.position, dimensions: fixture.dimensions, rotation: fixture.rotation, label: fixture.label }),
      });
    } catch (err) { console.warn('Failed to save fixture:', err); }
  };

  const updateFixtureAPI = async (fixture: Fixture) => {
    try {
      await authFetch(API_ENDPOINTS.restaurantFixture(restaurantId, fixture.fixtureId), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ position: fixture.position, dimensions: fixture.dimensions, rotation: fixture.rotation, label: fixture.label }),
      });
    } catch (err) { console.warn('Failed to update fixture:', err); }
  };

  const deleteFixtureAPI = async (fixtureId: string) => {
    try {
      await authFetch(API_ENDPOINTS.restaurantFixture(restaurantId, fixtureId), { method: 'DELETE' });
    } catch (err) { console.warn('Failed to delete fixture:', err); }
  };

  const saveFloorConfigAPI = async (config: { bgColor: string; bgPattern: string | null; sections: Section[] }) => {
    try {
      await authFetch(API_ENDPOINTS.floorConfig(restaurantId), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      });
    } catch (err) { console.warn('Failed to save floor config:', err); }
  };

  const createSectionTemplateAPI = async (template: SectionTemplate) => {
    try {
      await authFetch(API_ENDPOINTS.sectionTemplates(restaurantId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: template.name, sections: template.sections }),
      });
    } catch (err) { console.warn('Failed to save section template:', err); }
  };

  const deleteSectionTemplateAPI = async (templateId: string) => {
    try {
      await authFetch(API_ENDPOINTS.sectionTemplate(restaurantId, templateId), { method: 'DELETE' });
    } catch (err) { console.warn('Failed to delete section template:', err); }
  };

  // ─── Save All ──────────────────────────────────────────────────────────

  const saveAll = async () => {
    setIsSaving(true);
    try {
      // Only save tables that were actually modified
      const dirtyIds = dirtyTableIds.current;
      const tablesToSave = dirtyIds.size > 0
        ? tables.filter(t => dirtyIds.has(t.tableId))
        : [];

      // Save in batches of 5 to avoid overwhelming the API
      const BATCH_SIZE = 5;
      for (let i = 0; i < tablesToSave.length; i += BATCH_SIZE) {
        const batch = tablesToSave.slice(i, i + BATCH_SIZE);
        await Promise.all(batch.map(t => saveTable(t)));
      }
      dirtyTableIds.current.clear();

      await saveFloorConfigAPI({ bgColor, bgPattern, sections });
      await Promise.all(fixtures.map(f => updateFixtureAPI(f)));
      setIsDirty(false);
      setLastSaved(new Date().toLocaleTimeString());
    } catch (err) {
      console.warn('Save failed:', err);
      setError('Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // ─── Table Operations ───────────────────────────────────────────────────

  const addTableFromLibrary = async (libItem: TableType, x: number, y: number) => {
    pushUndo();
    const gridSize = currentLayout?.gridSize || GRID_SIZE;
    const shouldSnap = gridMode !== 'hidden';
    const posX = shouldSnap ? snapToGrid(x, gridSize) : x;
    const posY = shouldSnap ? snapToGrid(y, gridSize) : y;
    const newTable = normalizeTable({
      tableNumber: getNextTableNumber(tables),
      tableType: libItem.type,
      position: { x: posX, y: posY },
      dimensions: { width: libItem.width, height: libItem.height },
      capacity: { default: libItem.defaultCapacity, maximum: libItem.maxCapacity, current: 0 },
      shape: libItem.shape,
      status: 'available',
      spaceId: currentLayout?.layoutId,
    }, restaurantId);
    const created = await createTableAPI(newTable);
    setTables(prev => [...prev, created]);
    setSelectedTableId(created.tableId);
  };

  const updateTable = (tableId: string, updates: Partial<Table>) => {
    setTables(prev => prev.map(t => t.tableId === tableId ? { ...t, ...updates } : t));
    dirtyTableIds.current.add(tableId);
  };

  const deleteTable = async (tableId: string) => {
    pushUndo();
    setTables(prev => prev.filter(t => t.tableId !== tableId));
    if (selectedTableId === tableId) setSelectedTableId(null);
    await deleteTableAPI(tableId);
  };

  const moveTable = (tableId: string, x: number, y: number) => {
    const gridSize = currentLayout?.gridSize || GRID_SIZE;
    const shouldSnap = gridMode === 'snap' || gridMode === 'visible';
    const posX = shouldSnap ? snapToGrid(x, gridSize) : Math.round(x);
    const posY = shouldSnap ? snapToGrid(y, gridSize) : Math.round(y);
    updateTable(tableId, { position: { x: Math.max(0, posX), y: Math.max(0, posY) } });
  };

  const changeStatus = async (tableId: string, newStatus: TableStatus) => {
    pushUndo();
    const updates: Partial<Table> = {
      status: newStatus,
      statusChangedAt: new Date().toISOString(),
    };
    if (newStatus === 'available') {
      updates.partySize = undefined;
      updates.guestName = '';
      updates.serverName = '';
    }
    updateTable(tableId, updates);
    setStatusDropdown(null);
    await updateTableStatusAPI(tableId, newStatus);
  };

  // ─── Wall Drawing Finish ────────────────────────────────────────────────

  const finishWall = useCallback(() => {
    if (wallDrawPoints.length >= 2) {
      const newWall: Wall = {
        wallId: `wall_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        points: [...wallDrawPoints],
        thickness: wallThickness, color: wallColor, style: wallStyle, closed: false,
      };
      setWalls(prev => [...prev, newWall]);
      createWallAPI(newWall);
    }
    setWallDrawPoints([]);
    setWallPreviewPoint(null);
  }, [wallDrawPoints, wallThickness, wallColor, wallStyle]);

  // ─── Keyboard Shortcuts ─────────────────────────────────────────────────

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') return;

      if (e.key === 'Escape') {
        if (wallDrawPoints.length > 0) { setWallDrawPoints([]); setWallPreviewPoint(null); return; }
        setSelectedTableId(null);
        setSelectedTableIds(new Set());
        setSelectedFixtureId(null);
        setSelectedWallId(null);
        setStatusDropdown(null);
        setShowPropertiesModal(false);
        setContextMenu(null);
        return;
      }

      // Enter to finish wall drawing
      if (e.key === 'Enter' && editTool === 'wall' && wallDrawPoints.length >= 2) {
        e.preventDefault();
        finishWall();
        return;
      }

      // V key to switch to select tool
      if (e.key === 'v' && mode === 'edit' && !e.metaKey && !e.ctrlKey) {
        setEditTool('select');
        setWallDrawPoints([]);
        return;
      }

      // Delete multi-selected or single selected
      if ((e.key === 'Delete' || e.key === 'Backspace') && mode === 'edit') {
        e.preventDefault();
        if (selectedFixtureId) {
          deleteFixture(selectedFixtureId);
          setSelectedFixtureId(null);
          return;
        }
        if (selectedWallId) {
          deleteWall(selectedWallId);
          setSelectedWallId(null);
          return;
        }
        if (selectedTableIds.size > 0) {
          pushUndo();
          selectedTableIds.forEach(id => deleteTableAPI(id));
          setTables(prev => prev.filter(t => !selectedTableIds.has(t.tableId)));
          setSelectedTableIds(new Set());
          setSelectedTableId(null);
        } else if (selectedTableId) {
          deleteTable(selectedTableId);
        }
        return;
      }

      // Select all (Ctrl+A)
      if ((e.metaKey || e.ctrlKey) && e.key === 'a' && mode === 'edit') {
        e.preventDefault();
        setSelectedTableIds(new Set(tables.map(t => t.tableId)));
        return;
      }

      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault();
        undo();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'y') {
        e.preventDefault();
        redo();
        return;
      }

      // Arrow key nudge (works on multi-selection)
      if (mode === 'edit' && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        const ids = selectedTableIds.size > 0 ? Array.from(selectedTableIds) : (selectedTableId ? [selectedTableId] : []);
        if (ids.length === 0) return;
        e.preventDefault();
        const step = e.shiftKey ? 1 : (currentLayout?.gridSize || GRID_SIZE);
        pushUndo();
        ids.forEach(id => {
          const table = tables.find(t => t.tableId === id);
          if (!table) return;
          let { x, y } = table.position;
          if (e.key === 'ArrowUp') y -= step;
          if (e.key === 'ArrowDown') y += step;
          if (e.key === 'ArrowLeft') x -= step;
          if (e.key === 'ArrowRight') x += step;
          moveTable(id, x, y);
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedTableId, selectedTableIds, mode, editTool, tables, undo, redo, pushUndo, wallDrawPoints, finishWall]);

  // ─── Canvas Mouse Events (Pan & Table Drag) ────────────────────────────

  const getCanvasCoords = (clientX: number, clientY: number): { x: number; y: number } => {
    const wrapper = canvasWrapperRef.current;
    if (!wrapper) return { x: 0, y: 0 };
    const rect = wrapper.getBoundingClientRect();
    return {
      x: (clientX - rect.left - pan.x) / scale,
      y: (clientY - rect.top - pan.y) / scale,
    };
  };

  const handleCanvasMouseDown = (e: React.MouseEvent) => {
    setContextMenu(null);
    if (e.button !== 0) return;
    const isCanvas = (e.target as HTMLElement).classList.contains('tcm-canvas') || (e.target as HTMLElement).classList.contains('tcm-canvas-wrapper');

    // Wall tool click handling - accept clicks anywhere on the canvas area
    // (table/fixture clicks already call stopPropagation so they won't reach here)
    if (mode === 'edit' && editTool === 'wall') {
      const coords = getCanvasCoords(e.clientX, e.clientY);
      const snapped = wallDrawPoints.length > 0 ? snapWallAngle(wallDrawPoints[wallDrawPoints.length - 1], coords) : coords;
      // Close wall if clicking near start point
      if (wallDrawPoints.length >= 2) {
        const start = wallDrawPoints[0];
        if (Math.abs(snapped.x - start.x) < 15 && Math.abs(snapped.y - start.y) < 15) {
          const newWall: Wall = {
            wallId: `wall_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
            points: [...wallDrawPoints],
            thickness: wallThickness, color: wallColor, style: wallStyle, closed: true,
          };
          setWalls(prev => [...prev, newWall]);
          createWallAPI(newWall);
          setWallDrawPoints([]);
          setWallPreviewPoint(null);
          return;
        }
      }
      setWallDrawPoints(prev => [...prev, snapped]);
      return;
    }

    if (isCanvas) {
      // Clear fixture/wall selection on canvas click
      setSelectedFixtureId(null);
      setSelectedWallId(null);
      // Start selection rectangle if in edit mode with select tool
      if (mode === 'edit' && editTool === 'select' && !e.shiftKey) {
        const coords = getCanvasCoords(e.clientX, e.clientY);
        setIsDrawingSelection(true);
        setSelectionRect({ startX: coords.x, startY: coords.y, endX: coords.x, endY: coords.y });
        if (!e.shiftKey) {
          setSelectedTableId(null);
          setSelectedTableIds(new Set());
        }
      }
      setIsPanning(true);
      setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
      if (!e.shiftKey) {
        setSelectedTableId(null);
        setSelectedTableIds(new Set());
      }
      setStatusDropdown(null);
    }
  };

  const handleCanvasMouseMove = (e: React.MouseEvent) => {
    // Wall preview
    if (mode === 'edit' && editTool === 'wall' && wallDrawPoints.length > 0) {
      const coords = getCanvasCoords(e.clientX, e.clientY);
      setWallPreviewPoint(snapWallAngle(wallDrawPoints[wallDrawPoints.length - 1], coords));
    }

    // Selection rectangle
    if (isDrawingSelection && selectionRect) {
      const coords = getCanvasCoords(e.clientX, e.clientY);
      setSelectionRect(prev => prev ? { ...prev, endX: coords.x, endY: coords.y } : null);
      // Don't pan while drawing selection
      return;
    }

    if (isPanning && !isDrawingSelection) {
      setPan({ x: e.clientX - panStart.x, y: e.clientY - panStart.y });
    }

    // Resize handle drag
    if (isResizing && selectedTableId && resizeHandle) {
      const coords = getCanvasCoords(e.clientX, e.clientY);
      const table = tables.find(t => t.tableId === selectedTableId);
      if (!table) return;
      const gs = currentLayout?.gridSize || GRID_SIZE;
      const snap = gridMode === 'snap' || gridMode === 'visible';
      const sx = snap ? snapToGrid(coords.x, gs) : Math.round(coords.x);
      const sy = snap ? snapToGrid(coords.y, gs) : Math.round(coords.y);
      let newW = resizeStart.w;
      let newH = resizeStart.h;
      let newX = table.position.x;
      let newY = table.position.y;
      if (resizeHandle.includes('e')) newW = Math.max(gs, sx - table.position.x);
      if (resizeHandle.includes('s')) newH = Math.max(gs, sy - table.position.y);
      if (resizeHandle.includes('w')) {
        newW = Math.max(gs, resizeStart.w + (resizeStart.x - sx));
        newX = sx;
      }
      if (resizeHandle.includes('n')) {
        newH = Math.max(gs, resizeStart.h + (resizeStart.y - sy));
        newY = sy;
      }
      updateTable(selectedTableId, {
        dimensions: { width: newW, height: newH },
        position: { x: newX, y: newY },
      });
      return;
    }

    // Fixture dragging
    if (isDraggingFixture && selectedFixtureId) {
      const coords = getCanvasCoords(e.clientX, e.clientY);
      setFixtures(prev => prev.map(f =>
        f.fixtureId === selectedFixtureId
          ? { ...f, position: { x: Math.max(0, Math.round(coords.x - fixtureOffset.x)), y: Math.max(0, Math.round(coords.y - fixtureOffset.y)) } }
          : f
      ));
      return;
    }

    if (isDraggingTable) {
      const coords = getCanvasCoords(e.clientX, e.clientY);
      const gs = currentLayout?.gridSize || GRID_SIZE;
      const shouldSnap = gridMode === 'snap' || gridMode === 'visible';
      // Move all multi-selected tables
      if (selectedTableIds.size > 1 && selectedTableId) {
        const primary = tables.find(t => t.tableId === selectedTableId);
        if (primary) {
          const rawX = coords.x - dragOffset.x;
          const rawY = coords.y - dragOffset.y;
          const snappedX = shouldSnap ? snapToGrid(rawX, gs) : Math.round(rawX);
          const snappedY = shouldSnap ? snapToGrid(rawY, gs) : Math.round(rawY);
          // Use the snapped delta so all tables move uniformly
          const dx = snappedX - primary.position.x;
          const dy = snappedY - primary.position.y;
          selectedTableIds.forEach(id => {
            const t = tables.find(tt => tt.tableId === id);
            if (t) updateTable(id, { position: { x: Math.max(0, t.position.x + dx), y: Math.max(0, t.position.y + dy) } });
          });
          setAlignGuides(computeAlignGuides({ ...primary, position: { x: snappedX, y: snappedY } }, tables.filter(t => !selectedTableIds.has(t.tableId))));
        }
      } else if (selectedTableId) {
        const newX = coords.x - dragOffset.x;
        const newY = coords.y - dragOffset.y;
        moveTable(selectedTableId, newX, newY);
        const snappedX = shouldSnap ? snapToGrid(newX, gs) : Math.round(newX);
        const snappedY = shouldSnap ? snapToGrid(newY, gs) : Math.round(newY);
        const movedTable = tables.find(t => t.tableId === selectedTableId);
        if (movedTable) {
          setAlignGuides(computeAlignGuides({ ...movedTable, position: { x: snappedX, y: snappedY } }, tables));
        }
      }
    }
  };

  const handleCanvasMouseUp = () => {
    // Finish selection rectangle
    if (isDrawingSelection && selectionRect) {
      const minX = Math.min(selectionRect.startX, selectionRect.endX);
      const maxX = Math.max(selectionRect.startX, selectionRect.endX);
      const minY = Math.min(selectionRect.startY, selectionRect.endY);
      const maxY = Math.max(selectionRect.startY, selectionRect.endY);
      // Only select if rectangle has meaningful size
      if (maxX - minX > 5 || maxY - minY > 5) {
        const ids = new Set<string>();
        tables.forEach(t => {
          const cx = t.position.x + t.dimensions.width / 2;
          const cy = t.position.y + t.dimensions.height / 2;
          if (cx >= minX && cx <= maxX && cy >= minY && cy <= maxY) {
            ids.add(t.tableId);
          }
        });
        setSelectedTableIds(ids);
        if (ids.size === 1) setSelectedTableId(Array.from(ids)[0]);
      }
      setSelectionRect(null);
      setIsDrawingSelection(false);
      setIsPanning(false);
      return;
    }

    if (isDraggingFixture) {
      if (selectedFixtureId) {
        const fixture = fixtures.find(f => f.fixtureId === selectedFixtureId);
        if (fixture) updateFixtureAPI(fixture);
      }
      setIsDraggingFixture(false);
    }
    if (isDraggingTable) {
      if (selectedTableIds.size > 0) {
        selectedTableIds.forEach(id => {
          const t = tables.find(tt => tt.tableId === id);
          if (t) {
            saveTable(t).then(() => dirtyTableIds.current.delete(id)).catch(() => {});
          }
        });
      } else if (selectedTableId) {
        const table = tables.find(t => t.tableId === selectedTableId);
        if (table) {
          const id = selectedTableId;
          saveTable(table).then(() => dirtyTableIds.current.delete(id)).catch(() => {});
        }
      }
      setAlignGuides([]);
    }
    if (isResizing) {
      if (selectedTableId) {
        const table = tables.find(t => t.tableId === selectedTableId);
        if (table) {
          const id = selectedTableId;
          saveTable(table).then(() => dirtyTableIds.current.delete(id)).catch(() => {});
        }
      }
      setIsResizing(false);
      setResizeHandle(null);
    }
    setIsPanning(false);
    setIsDraggingTable(false);
  };

  // Right-click context menu
  const handleCanvasContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    const coords = getCanvasCoords(e.clientX, e.clientY);
    // Check if right-clicked on a table
    const clickedTable = tables.find(t => {
      return coords.x >= t.position.x && coords.x <= t.position.x + t.dimensions.width &&
             coords.y >= t.position.y && coords.y <= t.position.y + t.dimensions.height;
    });
    const wrapper = canvasWrapperRef.current;
    if (wrapper) {
      const rect = wrapper.getBoundingClientRect();
      setContextMenu({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
        tableId: clickedTable?.tableId || null,
      });
      if (clickedTable) {
        setSelectedTableId(clickedTable.tableId);
        if (!selectedTableIds.has(clickedTable.tableId)) {
          setSelectedTableIds(new Set([clickedTable.tableId]));
        }
      }
    }
  };

  // Wall drawing: double-click or Enter to finish (defined in keyboard shortcuts section above)

  const handleCanvasDoubleClick = (e: React.MouseEvent) => {
    if (mode === 'edit' && editTool === 'wall' && wallDrawPoints.length >= 2) {
      e.stopPropagation();
      finishWall();
    }
  };

  // Resize handle mouse down
  const handleResizeMouseDown = (e: React.MouseEvent, handle: string) => {
    e.stopPropagation();
    e.preventDefault();
    if (!selectedTable) return;
    pushUndo();
    setIsResizing(true);
    setResizeHandle(handle);
    setResizeStart({
      x: selectedTable.position.x,
      y: selectedTable.position.y,
      w: selectedTable.dimensions.width,
      h: selectedTable.dimensions.height,
    });
  };

  // Table drag on canvas (Edit Mode)
  const handleTableMouseDown = (e: React.MouseEvent, tableId: string) => {
    e.stopPropagation();
    setContextMenu(null);
    setStatusDropdown(null);

    // Shift+click for multi-select
    if (e.shiftKey && mode === 'edit') {
      setSelectedTableIds(prev => {
        const next = new Set(prev);
        if (next.has(tableId)) next.delete(tableId);
        else next.add(tableId);
        return next;
      });
      setSelectedTableId(tableId);
      return;
    }

    // Section Mode: assign table to active section
    if (mode === 'section' && activeSectionId) {
      setSections(prev => prev.map(s => {
        if (s.sectionId === activeSectionId) {
          const ids = s.tableIds.includes(tableId)
            ? s.tableIds.filter(id => id !== tableId)
            : [...s.tableIds, tableId];
          return { ...s, tableIds: ids };
        }
        // Remove from other sections
        return { ...s, tableIds: s.tableIds.filter(id => id !== tableId) };
      }));
      return;
    }

    setSelectedTableId(tableId);
    if (!selectedTableIds.has(tableId)) {
      setSelectedTableIds(new Set([tableId]));
    }

    if (mode === 'edit' && editTool === 'select') {
      const table = tables.find(t => t.tableId === tableId);
      if (!table) return;
      const coords = getCanvasCoords(e.clientX, e.clientY);
      setDragOffset({ x: coords.x - table.position.x, y: coords.y - table.position.y });
      pushUndo();
      setIsDraggingTable(true);
    }
  };

  const handleTableClick = (e: React.MouseEvent, tableId: string) => {
    e.stopPropagation();
    if (mode === 'live') {
      setSelectedTableId(tableId);
      const rect = canvasWrapperRef.current?.getBoundingClientRect();
      if (rect) {
        setStatusDropdown({
          tableId,
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        });
      }
    }
  };

  const handleTableDoubleClick = (e: React.MouseEvent, tableId: string) => {
    e.stopPropagation();
    if (mode === 'edit') {
      const table = tables.find(t => t.tableId === tableId);
      if (table) {
        setModalTable({ ...table });
        setShowPropertiesModal(true);
      }
    }
  };

  // ─── Library Drag & Drop ────────────────────────────────────────────────

  const handleLibraryDragStart = (e: React.DragEvent, item: TableType) => {
    setDragLibraryItem(item);
    setIsDraggingFromLibrary(true);
    e.dataTransfer.setData('text/plain', item.type);
    e.dataTransfer.effectAllowed = 'copy';
    // Hide default ghost
    const ghost = document.createElement('div');
    ghost.style.opacity = '0';
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 0, 0);
    setTimeout(() => document.body.removeChild(ghost), 0);
  };

  const handleCanvasDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setShowDropIndicator(true);
    setDragGhostPos({ x: e.clientX, y: e.clientY });
  };

  const handleCanvasDragLeave = () => {
    setShowDropIndicator(false);
  };

  const handleCanvasDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setShowDropIndicator(false);
    setIsDraggingFromLibrary(false);
    if (dragLibraryItem) {
      const coords = getCanvasCoords(e.clientX, e.clientY);
      await addTableFromLibrary(dragLibraryItem, coords.x - dragLibraryItem.width / 2, coords.y - dragLibraryItem.height / 2);
      setDragLibraryItem(null);
    }
  };

  // ─── Zoom ───────────────────────────────────────────────────────────────

  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.05 : 0.05;
      setScale(s => Math.min(2, Math.max(0.25, s + delta)));
    }
  }, []);

  const zoomIn = () => setScale(s => Math.min(2, s + 0.1));
  const zoomOut = () => setScale(s => Math.max(0.25, s - 0.1));
  const zoomReset = () => { setScale(0.75); setPan({ x: 20, y: 20 }); };

  // ─── Properties Sidebar Save ────────────────────────────────────────────

  const saveSelectedTable = async () => {
    if (!selectedTable) return;
    await saveTable(selectedTable);
    dirtyTableIds.current.delete(selectedTable.tableId);
  };

  // ─── Modal Save ─────────────────────────────────────────────────────────

  const saveModalTable = async () => {
    if (!modalTable) return;
    pushUndo();
    updateTable(modalTable.tableId, modalTable);
    await saveTable(modalTable);
    dirtyTableIds.current.delete(modalTable.tableId);
    setShowPropertiesModal(false);
    setModalTable(null);
  };

  // ─── Context Menu Actions ──────────────────────────────────────────────

  const duplicateTable = async (tableId: string) => {
    const table = tables.find(t => t.tableId === tableId);
    if (!table) return;
    pushUndo();
    const newTable = normalizeTable({
      ...table,
      tableId: undefined,
      tableNumber: getNextTableNumber(tables),
      position: { x: table.position.x + 30, y: table.position.y + 30 },
    }, restaurantId);
    const created = await createTableAPI(newTable);
    setTables(prev => [...prev, created]);
    setSelectedTableId(created.tableId);
    setSelectedTableIds(new Set([created.tableId]));
    setContextMenu(null);
  };

  const rotateTable = (tableId: string, degrees: number) => {
    pushUndo();
    const table = tables.find(t => t.tableId === tableId);
    if (!table) return;
    updateTable(tableId, { rotation: (table.rotation + degrees + 360) % 360 });
    setContextMenu(null);
  };

  // ─── Fixture Operations ──────────────────────────────────────────────

  const addFixture = (type: FixtureType, x: number, y: number) => {
    const libItem = FIXTURE_LIBRARY.find(f => f.type === type);
    if (!libItem) return;
    const fixture: Fixture = {
      fixtureId: `fixture_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      type,
      position: { x, y },
      dimensions: { width: libItem.width, height: libItem.height },
      rotation: 0,
      label: libItem.label,
    };
    setFixtures(prev => [...prev, fixture]);
    createFixtureAPI(fixture);
  };

  const deleteFixture = (fixtureId: string) => {
    setFixtures(prev => prev.filter(f => f.fixtureId !== fixtureId));
    deleteFixtureAPI(fixtureId);
  };

  const deleteWall = (wallId: string) => {
    setWalls(prev => prev.filter(w => w.wallId !== wallId));
    deleteWallAPI(wallId);
  };

  // ─── Section Operations ──────────────────────────────────────────────

  const addSection = () => {
    const idx = sections.length;
    const newSection: Section = {
      sectionId: `sec_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      name: `Section ${idx + 1}`,
      color: SECTION_COLORS[idx % SECTION_COLORS.length],
      serverName: '',
      tableIds: [],
    };
    setSections(prev => [...prev, newSection]);
    setActiveSectionId(newSection.sectionId);
  };

  const removeSection = (sectionId: string) => {
    setSections(prev => prev.filter(s => s.sectionId !== sectionId));
    if (activeSectionId === sectionId) setActiveSectionId(null);
  };

  const updateSection = (sectionId: string, updates: Partial<Section>) => {
    setSections(prev => prev.map(s => s.sectionId === sectionId ? { ...s, ...updates } : s));
  };

  const saveSectionTemplate = () => {
    const name = `Template ${sectionTemplates.length + 1}`;
    const template: SectionTemplate = {
      templateId: `section-template_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      name,
      sections: sections.map(s => ({ ...s })),
      createdAt: new Date().toISOString(),
    };
    setSectionTemplates(prev => [...prev, template]);
    createSectionTemplateAPI(template);
  };

  const applySectionTemplate = (templateId: string) => {
    const template = sectionTemplates.find(t => t.templateId === templateId);
    if (template) {
      setSections(template.sections.map(s => ({ ...s })));
    }
  };

  const deleteSectionTemplate = (templateId: string) => {
    setSectionTemplates(prev => prev.filter(t => t.templateId !== templateId));
    deleteSectionTemplateAPI(templateId);
  };

  const getSectionForTable = (tableId: string): Section | undefined => {
    return sections.find(s => s.tableIds.includes(tableId));
  };

  // ─── Computed Values ────────────────────────────────────────────────────

  // Filter tables by current space, then by selected area
  const spaceTables = currentLayout
    ? tables.filter(t => {
        // Tables without a spaceId belong to the first (default) space
        if (!t.spaceId) return layouts.length === 0 || currentLayout.layoutId === layouts[0]?.layoutId;
        return t.spaceId === currentLayout.layoutId;
      })
    : tables;
  const filteredTables = selectedArea
    ? spaceTables.filter(t => t.areaId === selectedArea)
    : spaceTables;

  const statusCounts = ALL_STATUSES.reduce((acc, s) => {
    acc[s] = spaceTables.filter(t => t.status === s).length;
    return acc;
  }, {} as Record<TableStatus, number>);

  const totalCovers = tables.reduce((sum, t) => sum + (t.partySize || 0), 0);
  const totalCapacity = tables.reduce((sum, t) => sum + t.capacity.maximum, 0);
  const dirtyCount = statusCounts.dirty + statusCounts.cleaning;

  const categories = Array.from(new Set(tableLibrary.map(t => t.category || 'Other')));

  // ─── Grid SVG ───────────────────────────────────────────────────────────

  const gridSize = currentLayout?.gridSize || GRID_SIZE;

  const renderGrid = () => {
    if (gridMode === 'hidden' || gridMode === 'snap') return null;
    return (
      <svg className="tcm-grid-overlay" width={canvasSize.width} height={canvasSize.height}>
        <defs>
          <pattern id="tcm-grid-pattern" width={gridSize} height={gridSize} patternUnits="userSpaceOnUse">
            <path d={`M ${gridSize} 0 L 0 0 0 ${gridSize}`} fill="none" stroke="#94a3b8" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#tcm-grid-pattern)" />
      </svg>
    );
  };

  // ─── Table Rendering ────────────────────────────────────────────────────

  const renderTable = (table: Table) => {
    const cfg = STATUS_CONFIG[table.status] || STATUS_CONFIG.available;
    const isSelected = table.tableId === selectedTableId || selectedTableIds.has(table.tableId);
    const shapeClass = `tcm-table--${table.shape}`;
    const statusClass = `tcm-table--${table.status}`;
    const inactiveClass = !table.active ? 'tcm-table--inactive' : '';
    const section = getSectionForTable(table.tableId);

    const style: React.CSSProperties = {
      left: table.position.x,
      top: table.position.y,
      width: table.dimensions.width,
      height: table.dimensions.height,
      transform: table.rotation ? `rotate(${table.rotation}deg)` : undefined,
    };
    if (table.colorOverride) {
      style.backgroundColor = table.colorOverride;
      style.borderColor = table.colorOverride;
    }
    // Section color wash
    if (section && (mode === 'section' || mode === 'live')) {
      style.boxShadow = `inset 0 0 0 100px ${section.color}33`;
    }

    // Detail scaling by zoom level
    const showMinimal = scale < 0.5;
    const showMedium = scale >= 0.5 && scale <= 1.0;
    const showFull = scale > 1.0;

    return (
      <div
        key={table.tableId}
        className={`tcm-table ${shapeClass} ${statusClass} ${inactiveClass} ${isSelected ? 'selected' : ''}`}
        style={style}
        onMouseDown={(e) => handleTableMouseDown(e, table.tableId)}
        onClick={(e) => handleTableClick(e, table.tableId)}
        onDoubleClick={(e) => handleTableDoubleClick(e, table.tableId)}
      >
        {table.isJoined && (
          <span className="tcm-join-indicator">{table.joinedWith.length + 1}</span>
        )}
        <div className="tcm-table__content">
          <span className="tcm-table__number">{table.tableNumber}</span>
          {mode === 'edit' && (
            <span className="tcm-table__seats">{table.capacity.default} seats</span>
          )}
          {mode === 'live' && (
            <>
              {!showMinimal && <span className="tcm-table__status-label">{cfg.label}</span>}
              {(showMedium || showFull) && table.partySize != null && table.partySize > 0 && (
                <span className="tcm-table__seats">{table.partySize} guests</span>
              )}
              {showFull && table.serverName && (
                <span className="tcm-table__guest">{table.serverName}</span>
              )}
              {showFull && table.guestName && (
                <span className="tcm-table__guest">{table.guestName}</span>
              )}
            </>
          )}
          {mode === 'section' && section && (
            <span className="tcm-table__seats" style={{ color: section.color }}>{section.name}</span>
          )}
        </div>
        {mode === 'live' && !showMinimal && table.partySize != null && table.partySize > 0 && (
          <span className="tcm-table__party">{table.partySize}</span>
        )}
        {mode === 'live' && (showMedium || showFull) && table.statusChangedAt && ['occupied', 'dirty', 'cleaning'].includes(table.status) && (
          <span className="tcm-table__timer">{formatTimer(table.statusChangedAt)}</span>
        )}
        {/* Resize handles (Edit Mode, single selection) */}
        {isSelected && mode === 'edit' && editTool === 'select' && selectedTableIds.size <= 1 && (
          <>
            <div className="tcm-resize-handle tcm-resize-handle--nw" onMouseDown={(e) => handleResizeMouseDown(e, 'nw')} />
            <div className="tcm-resize-handle tcm-resize-handle--ne" onMouseDown={(e) => handleResizeMouseDown(e, 'ne')} />
            <div className="tcm-resize-handle tcm-resize-handle--sw" onMouseDown={(e) => handleResizeMouseDown(e, 'sw')} />
            <div className="tcm-resize-handle tcm-resize-handle--se" onMouseDown={(e) => handleResizeMouseDown(e, 'se')} />
            <div className="tcm-rotation-handle" onMouseDown={(e) => {
              e.stopPropagation();
              // Simple rotation: cycle by 45 degrees
              pushUndo();
              updateTable(table.tableId, { rotation: (table.rotation + 45) % 360 });
            }} title="Click to rotate 45deg" />
          </>
        )}
      </div>
    );
  };

  // ─── Wall Rendering ────────────────────────────────────────────────────

  const renderWalls = () => {
    return (
      <svg className={`tcm-walls-layer ${mode === 'edit' ? 'tcm-walls-layer--interactive' : ''}`} width={canvasSize.width} height={canvasSize.height}>
        {walls.map(wall => {
          const pts = wall.points.map(p => `${p.x},${p.y}`).join(' ');
          const dashArray = wall.style === 'glass' ? '8,4' : wall.style === 'railing' ? '4,4' : undefined;
          const opacity = wall.style === 'half-wall' ? 0.5 : 1;
          const isSelected = wall.wallId === selectedWallId;
          const handleWallClick = (e: React.MouseEvent<SVGElement>) => {
            if (mode !== 'edit') return;
            e.stopPropagation();
            setSelectedWallId(wall.wallId);
            setSelectedFixtureId(null);
            setSelectedTableId(null);
            setSelectedTableIds(new Set());
          };
          const handleWallContext = (e: React.MouseEvent<SVGElement>) => {
            if (mode !== 'edit') return;
            e.preventDefault();
            e.stopPropagation();
            setSelectedWallId(wall.wallId);
            const wrapper = canvasWrapperRef.current;
            if (wrapper) {
              const rect = wrapper.getBoundingClientRect();
              setContextMenu({ x: e.clientX - rect.left, y: e.clientY - rect.top, tableId: null });
            }
          };
          // Render a wider invisible hit area + the visible wall
          return (
            <g key={wall.wallId}>
              {/* Invisible wider hit area for easier clicking */}
              {wall.closed ? (
                <polygon
                  points={pts}
                  fill="transparent"
                  stroke="transparent"
                  strokeWidth={Math.max(wall.thickness + 10, 16)}
                  style={{ cursor: mode === 'edit' ? 'pointer' : 'default', pointerEvents: mode === 'edit' ? 'stroke' : 'none' }}
                  onMouseDown={handleWallClick}
                  onContextMenu={handleWallContext}
                />
              ) : (
                <polyline
                  points={pts}
                  fill="none"
                  stroke="transparent"
                  strokeWidth={Math.max(wall.thickness + 10, 16)}
                  strokeLinecap="round"
                  style={{ cursor: mode === 'edit' ? 'pointer' : 'default', pointerEvents: mode === 'edit' ? 'stroke' : 'none' }}
                  onMouseDown={handleWallClick}
                  onContextMenu={handleWallContext}
                />
              )}
              {/* Visible wall */}
              {wall.closed ? (
                <polygon
                  points={pts}
                  fill={`${wall.color}22`}
                  stroke={isSelected ? '#3b82f6' : wall.color}
                  strokeWidth={isSelected ? wall.thickness + 2 : wall.thickness}
                  strokeDasharray={dashArray}
                  opacity={opacity}
                  style={{ pointerEvents: 'none' }}
                />
              ) : (
                <polyline
                  points={pts}
                  fill="none"
                  stroke={isSelected ? '#3b82f6' : wall.color}
                  strokeWidth={isSelected ? wall.thickness + 2 : wall.thickness}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeDasharray={dashArray}
                  opacity={opacity}
                  style={{ pointerEvents: 'none' }}
                />
              )}
              {/* Selection indicators on wall points */}
              {isSelected && wall.points.map((p, i) => (
                <circle key={i} cx={p.x} cy={p.y} r={5} fill="#fff" stroke="#3b82f6" strokeWidth={2} style={{ pointerEvents: 'none' }} />
              ))}
            </g>
          );
        })}
        {/* Wall drawing preview */}
        {wallDrawPoints.length > 0 && (
          <polyline
            points={[...wallDrawPoints, ...(wallPreviewPoint ? [wallPreviewPoint] : [])].map(p => `${p.x},${p.y}`).join(' ')}
            fill="none"
            stroke="#3b82f6"
            strokeWidth={wallThickness}
            strokeDasharray="6,3"
            opacity={0.6}
            style={{ pointerEvents: 'none' }}
          />
        )}
        {wallDrawPoints.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={4} fill="#3b82f6" style={{ pointerEvents: 'none' }} />
        ))}
      </svg>
    );
  };

  // ─── Fixture Rendering ─────────────────────────────────────────────────

  const renderFixtures = () => {
    return fixtures.map(fixture => {
      const libItem = FIXTURE_LIBRARY.find(f => f.type === fixture.type);
      const isSelected = fixture.fixtureId === selectedFixtureId;
      return (
        <div
          key={fixture.fixtureId}
          className={`tcm-fixture ${isSelected ? 'tcm-fixture--selected' : ''}`}
          style={{
            left: fixture.position.x,
            top: fixture.position.y,
            width: fixture.dimensions.width,
            height: fixture.dimensions.height,
            transform: fixture.rotation ? `rotate(${fixture.rotation}deg)` : undefined,
          }}
          onMouseDown={(e) => {
            if (mode !== 'edit') return;
            e.stopPropagation();
            setSelectedFixtureId(fixture.fixtureId);
            setSelectedWallId(null);
            setSelectedTableId(null);
            setSelectedTableIds(new Set());
            const coords = getCanvasCoords(e.clientX, e.clientY);
            setFixtureOffset({ x: coords.x - fixture.position.x, y: coords.y - fixture.position.y });
            setIsDraggingFixture(true);
          }}
          onContextMenu={(e) => {
            e.preventDefault();
            if (mode === 'edit') {
              setSelectedFixtureId(fixture.fixtureId);
              const wrapper = canvasWrapperRef.current;
              if (wrapper) {
                const rect = wrapper.getBoundingClientRect();
                setContextMenu({ x: e.clientX - rect.left, y: e.clientY - rect.top, tableId: null });
              }
            }
          }}
        >
          <span className="tcm-fixture__icon">{libItem?.icon || FixtureIcons[fixture.type] || '?'}</span>
          <span className="tcm-fixture__label">{fixture.label}</span>
        </div>
      );
    });
  };

  // ─── Merge Connectors ──────────────────────────────────────────────────

  const renderMergeConnectors = () => {
    const connectors: React.ReactNode[] = [];
    const processed = new Set<string>();
    tables.forEach(table => {
      if (table.isJoined && table.joinedWith.length > 0) {
        table.joinedWith.forEach(partnerId => {
          const key = [table.tableId, partnerId].sort().join('-');
          if (processed.has(key)) return;
          processed.add(key);
          const partner = tables.find(t => t.tableId === partnerId);
          if (!partner) return;
          const x1 = table.position.x + table.dimensions.width / 2;
          const y1 = table.position.y + table.dimensions.height / 2;
          const x2 = partner.position.x + partner.dimensions.width / 2;
          const y2 = partner.position.y + partner.dimensions.height / 2;
          connectors.push(
            <line key={key} x1={x1} y1={y1} x2={x2} y2={y2}
              stroke="#8b5cf6" strokeWidth={3} strokeDasharray="6,3" />
          );
          const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
          connectors.push(
            <text key={`${key}-label`} x={mx} y={my - 6} textAnchor="middle"
              fontSize={11} fontWeight={700} fill="#8b5cf6">
              {table.capacity.default + partner.capacity.default} seats
            </text>
          );
        });
      }
    });
    if (connectors.length === 0) return null;
    return (
      <svg className="tcm-merge-layer" width={canvasSize.width} height={canvasSize.height}>
        {connectors}
      </svg>
    );
  };

  // ─── Sidebar: Edit Mode ─────────────────────────────────────────────────

  const renderEditSidebar = () => (
    <div className="tcm-sidebar">
      {/* Tool selector */}
      <div className="tcm-sidebar-section">
        <h3>Tools</h3>
        <div className="tcm-tool-bar">
          <button className={`tcm-tool-btn ${editTool === 'select' ? 'active' : ''}`} onClick={() => { setEditTool('select'); setWallDrawPoints([]); }} title="Select (V)">
            <span>&#9654;</span> Select
          </button>
          <button className={`tcm-tool-btn ${editTool === 'wall' ? 'active' : ''}`} onClick={() => setEditTool('wall')} title="Wall Tool">
            <span>&#9647;</span> Wall
          </button>
          <button className={`tcm-tool-btn ${editTool === 'fixture' ? 'active' : ''}`} onClick={() => setEditTool('fixture')} title="Fixture Tool">
            <span>&#9635;</span> Fixture
          </button>
        </div>
      </div>

      {/* Wall properties (when wall tool active) */}
      {editTool === 'wall' && (
        <div className="tcm-sidebar-section">
          <h3>Wall Properties</h3>
          <div className="tcm-prop-group">
            <label>Thickness</label>
            <select value={wallThickness} onChange={e => setWallThickness(Number(e.target.value))}>
              <option value={3}>Thin (3px)</option>
              <option value={6}>Medium (6px)</option>
              <option value={12}>Thick (12px)</option>
            </select>
          </div>
          <div className="tcm-prop-group">
            <label>Style</label>
            <select value={wallStyle} onChange={e => setWallStyle(e.target.value as WallStyle)}>
              <option value="solid">Solid</option>
              <option value="half-wall">Half Wall</option>
              <option value="glass">Glass</option>
              <option value="railing">Railing</option>
            </select>
          </div>
          <div className="tcm-prop-group">
            <label>Color</label>
            <input type="color" value={wallColor} onChange={e => setWallColor(e.target.value)} style={{ width: 40, height: 28 }} />
          </div>
          {wallDrawPoints.length > 0 && (
            <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
              <button className="tcm-btn tcm-btn--primary tcm-btn--small" onClick={finishWall}>Finish Wall</button>
              <button className="tcm-btn tcm-btn--secondary tcm-btn--small" onClick={() => { setWallDrawPoints([]); setWallPreviewPoint(null); }}>Cancel</button>
            </div>
          )}
          {walls.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <label style={{ fontSize: 11, color: '#6b7280' }}>Walls ({walls.length})</label>
              {walls.map(w => (
                <div key={w.wallId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                  <span style={{ fontSize: 12 }}>{w.closed ? 'Room' : 'Wall'} ({w.points.length} pts)</span>
                  <button className="tcm-btn tcm-btn--danger tcm-btn--small" onClick={() => deleteWall(w.wallId)} style={{ padding: '2px 6px', fontSize: 10 }}>x</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Fixture library (when fixture tool active) */}
      {editTool === 'fixture' && (
        <div className="tcm-sidebar-section">
          <h3>Fixtures</h3>
          <div className="tcm-library-grid">
            {FIXTURE_LIBRARY.map(item => (
              <div
                key={item.type}
                className="tcm-library-item"
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('fixture-type', item.type);
                  e.dataTransfer.effectAllowed = 'copy';
                }}
              >
                <div className="tcm-library-preview">
                  <div className="tcm-fixture-preview">{item.icon}</div>
                </div>
                <span className="tcm-library-name">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Table library (when select tool active) */}
      {editTool === 'select' && (
        <div className="tcm-sidebar-section">
          <h3>Table Library</h3>
          {categories.map(cat => {
            const items = tableLibrary.filter(t => (t.category || 'Other') === cat);
            const isCollapsed = collapsedCategories.has(cat);
            return (
              <div key={cat} className="tcm-library-category">
                <div
                  className="tcm-library-category-header"
                  onClick={() => setCollapsedCategories(prev => {
                    const next = new Set(prev);
                    if (next.has(cat)) next.delete(cat); else next.add(cat);
                    return next;
                  })}
                >
                  <span>{cat} ({items.length})</span>
                  <span className={`tcm-library-category-arrow ${isCollapsed ? 'collapsed' : ''}`}>&#9660;</span>
                </div>
                {!isCollapsed && (
                  <div className="tcm-library-grid">
                    {items.map(item => (
                      <div
                        key={item.type}
                        className="tcm-library-item"
                        draggable
                        onDragStart={(e) => handleLibraryDragStart(e, item)}
                      >
                        <div className="tcm-library-preview">
                          <div className={`tcm-preview-shape tcm-preview-shape--${item.shape}`} />
                        </div>
                        <span className="tcm-library-name">{item.displayName}</span>
                        <span className="tcm-library-seats">{item.defaultCapacity} seats</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Background */}
      <div className="tcm-sidebar-section">
        <h3>Background</h3>
        <div className="tcm-bg-presets">
          {BACKGROUND_PRESETS.map(p => (
            <div
              key={p.color}
              className={`tcm-bg-swatch ${bgColor === p.color ? 'active' : ''}`}
              style={{ background: p.color }}
              onClick={() => setBgColor(p.color)}
              title={p.name}
            />
          ))}
        </div>
        <div className="tcm-prop-group" style={{ marginTop: 8 }}>
          <label>Custom</label>
          <input type="color" value={bgColor} onChange={e => setBgColor(e.target.value)} style={{ width: 40, height: 28 }} />
        </div>
        <div className="tcm-prop-group">
          <label>Pattern</label>
          <select value={bgPattern || ''} onChange={e => setBgPattern(e.target.value || null)}>
            <option value="">None</option>
            <option value="hardwood">Hardwood</option>
            <option value="tile">Tile</option>
            <option value="carpet">Carpet</option>
            <option value="concrete">Concrete</option>
          </select>
        </div>
      </div>

      {/* Areas */}
      <div className="tcm-sidebar-section">
        <h3>Areas</h3>
        <div className="tcm-areas-list">
          <div
            className={`tcm-area-item ${!selectedArea ? 'selected' : ''}`}
            onClick={() => setSelectedArea(null)}
          >
            <div className="tcm-area-color" style={{ background: '#6b7280' }} />
            <span>All Areas</span>
            <span className="tcm-area-count">{tables.length}</span>
          </div>
          {areas.map(area => (
            <div
              key={area.areaId}
              className={`tcm-area-item ${selectedArea === area.areaId ? 'selected' : ''}`}
              onClick={() => setSelectedArea(prev => prev === area.areaId ? null : area.areaId)}
            >
              <div className="tcm-area-color" style={{ background: area.color }} />
              <span>{area.name}</span>
              <span className="tcm-area-count">{tables.filter(t => t.areaId === area.areaId).length}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ─── Sidebar: Live Mode ─────────────────────────────────────────────────

  const renderLiveSidebar = () => (
    <div className="tcm-sidebar">
      <div className="tcm-sidebar-section">
        <h3>Status Legend</h3>
        <div className="tcm-status-legend">
          {ALL_STATUSES.map(s => (
            <div key={s} className="tcm-status-legend-item">
              <div className="tcm-status-swatch" style={{ background: STATUS_CONFIG[s].border }} />
              <span>{STATUS_CONFIG[s].label}</span>
              <span className="tcm-status-count">{statusCounts[s]}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="tcm-sidebar-section">
        <h3>Live Stats</h3>
        <div className="tcm-live-stats">
          <div className="tcm-live-stat">
            <span className="tcm-live-stat-label">Covers / Capacity</span>
            <span className="tcm-live-stat-value">{totalCovers} / {totalCapacity}</span>
          </div>
          <div className="tcm-live-stat">
            <span className="tcm-live-stat-label">Available</span>
            <span className="tcm-live-stat-value">{statusCounts.available} / {tables.length}</span>
          </div>
          <div className="tcm-live-stat">
            <span className="tcm-live-stat-label">Dirty / Cleaning</span>
            <span className={`tcm-live-stat-value ${dirtyCount > 3 ? 'urgent' : ''}`}>{dirtyCount}</span>
          </div>
          <div className="tcm-live-stat">
            <span className="tcm-live-stat-label">Occupied</span>
            <span className="tcm-live-stat-value">{statusCounts.occupied}</span>
          </div>
        </div>
      </div>
    </div>
  );

  // ─── Sidebar: Section Mode ──────────────────────────────────────────────

  const renderSectionSidebar = () => (
    <div className="tcm-sidebar">
      <div className="tcm-sidebar-section">
        <h3>Sections</h3>
        <button className="tcm-btn tcm-btn--primary tcm-btn--small" onClick={addSection} style={{ width: '100%', marginBottom: 8 }}>
          + Add Section
        </button>
        <div className="tcm-sections-list">
          {sections.map(section => (
            <div
              key={section.sectionId}
              className={`tcm-section-card ${activeSectionId === section.sectionId ? 'active' : ''}`}
              onClick={() => setActiveSectionId(prev => prev === section.sectionId ? null : section.sectionId)}
              style={{ borderLeftColor: section.color }}
            >
              <div className="tcm-section-card-header">
                <div className="tcm-area-color" style={{ background: section.color }} />
                <input
                  className="tcm-section-name-input"
                  value={section.name}
                  onChange={e => updateSection(section.sectionId, { name: e.target.value })}
                  onClick={e => e.stopPropagation()}
                />
                <button
                  className="tcm-btn tcm-btn--danger tcm-btn--small"
                  onClick={(e) => { e.stopPropagation(); removeSection(section.sectionId); }}
                  style={{ padding: '2px 6px', fontSize: 10, minWidth: 'auto' }}
                >x</button>
              </div>
              <div className="tcm-section-card-body">
                <div className="tcm-prop-group" style={{ marginBottom: 4 }}>
                  <label style={{ fontSize: 10 }}>Server</label>
                  <input
                    value={section.serverName}
                    onChange={e => updateSection(section.sectionId, { serverName: e.target.value })}
                    onClick={e => e.stopPropagation()}
                    placeholder="Assign server..."
                    style={{ padding: '4px 6px', fontSize: 12 }}
                  />
                </div>
                <div style={{ fontSize: 11, color: '#6b7280' }}>
                  {section.tableIds.length} tables | {section.tableIds.reduce((sum, id) => {
                    const t = tables.find(tt => tt.tableId === id);
                    return sum + (t?.capacity.maximum || 0);
                  }, 0)} covers
                </div>
                <div className="tcm-prop-group" style={{ marginTop: 4 }}>
                  <label style={{ fontSize: 10 }}>Color</label>
                  <div style={{ display: 'flex', gap: 3 }}>
                    {SECTION_COLORS.map(c => (
                      <div
                        key={c}
                        className={`tcm-bg-swatch ${section.color === c ? 'active' : ''}`}
                        style={{ background: c, width: 18, height: 18 }}
                        onClick={(e) => { e.stopPropagation(); updateSection(section.sectionId, { color: c }); }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {activeSectionId && (
          <div style={{ fontSize: 11, color: '#3b82f6', marginTop: 8, padding: '6px 8px', background: '#eff6ff', borderRadius: 4 }}>
            Click tables on the canvas to assign them to the selected section.
          </div>
        )}
      </div>
      <div className="tcm-sidebar-section">
        <h3>Templates</h3>
        <button
          className="tcm-btn tcm-btn--secondary tcm-btn--small"
          onClick={saveSectionTemplate}
          disabled={sections.length === 0}
          style={{ width: '100%', marginBottom: 8 }}
        >
          Save Current as Template
        </button>
        {sectionTemplates.map(tmpl => (
          <div key={tmpl.templateId} className="tcm-template-card">
            <span style={{ fontSize: 12, fontWeight: 500 }}>{tmpl.name}</span>
            <span style={{ fontSize: 10, color: '#6b7280' }}>{tmpl.sections.length} sections</span>
            <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
              <button className="tcm-btn tcm-btn--primary tcm-btn--small" onClick={() => applySectionTemplate(tmpl.templateId)} style={{ fontSize: 10, padding: '2px 6px' }}>Apply</button>
              <button className="tcm-btn tcm-btn--danger tcm-btn--small" onClick={() => deleteSectionTemplate(tmpl.templateId)} style={{ fontSize: 10, padding: '2px 6px' }}>Delete</button>
            </div>
          </div>
        ))}
      </div>
      {/* Section balance summary */}
      {sections.length > 0 && (
        <div className="tcm-sidebar-section">
          <h3>Balance Summary</h3>
          {sections.map(section => {
            const tableCount = section.tableIds.length;
            const covers = section.tableIds.reduce((sum, id) => {
              const t = tables.find(tt => tt.tableId === id);
              return sum + (t?.capacity.maximum || 0);
            }, 0);
            return (
              <div key={section.sectionId} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12 }}>
                <span style={{ color: section.color, fontWeight: 500 }}>{section.name}</span>
                <span>{tableCount} tables / {covers} covers</span>
              </div>
            );
          })}
          {(() => {
            const counts = sections.map(s => s.tableIds.length);
            const max = Math.max(...counts, 0);
            const min = Math.min(...counts, 0);
            if (max - min > 2 && sections.length > 1) {
              return <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>Warning: Sections are imbalanced</div>;
            }
            return null;
          })()}
        </div>
      )}
    </div>
  );

  // ─── Properties Panel (right sidebar) ───────────────────────────────────

  const renderPropertiesPanel = () => {
    if (!selectedTable || mode !== 'edit') return null;

    const handleChange = (field: string, value: any) => {
      updateTable(selectedTable.tableId, { [field]: value });
    };

    return (
      <div className="tcm-properties">
        <h3>Table {selectedTable.tableNumber}</h3>

        <div className="tcm-prop-row">
          <div className="tcm-prop-group">
            <label>Table Number</label>
            <input
              value={selectedTable.tableNumber}
              onChange={e => handleChange('tableNumber', e.target.value)}
              onBlur={saveSelectedTable}
            />
          </div>
          <div className="tcm-prop-group">
            <label>Display Name</label>
            <input
              value={selectedTable.displayName || ''}
              onChange={e => handleChange('displayName', e.target.value)}
              onBlur={saveSelectedTable}
              placeholder="Optional"
            />
          </div>
        </div>

        <div className="tcm-prop-group">
          <label>Shape</label>
          <select
            value={selectedTable.shape}
            onChange={e => { pushUndo(); handleChange('shape', e.target.value); }}
          >
            <option value="square">Square</option>
            <option value="circle">Round</option>
            <option value="rectangle">Rectangle</option>
            <option value="oval">Oval</option>
            <option value="booth">Booth</option>
            <option value="bar">Bar</option>
          </select>
        </div>

        <div className="tcm-prop-row">
          <div className="tcm-prop-group">
            <label>Default Seats</label>
            <input
              type="number" min="1" max="50"
              value={selectedTable.capacity.default}
              onChange={e => {
                const val = parseInt(e.target.value, 10) || 1;
                handleChange('capacity', { ...selectedTable.capacity, default: val });
              }}
              onBlur={saveSelectedTable}
            />
          </div>
          <div className="tcm-prop-group">
            <label>Max Seats</label>
            <input
              type="number" min="1" max="50"
              value={selectedTable.capacity.maximum}
              onChange={e => {
                const val = parseInt(e.target.value, 10) || 1;
                handleChange('capacity', { ...selectedTable.capacity, maximum: val });
              }}
              onBlur={saveSelectedTable}
            />
          </div>
        </div>

        <div className="tcm-prop-row">
          <div className="tcm-prop-group">
            <label>Min Covers</label>
            <input
              type="number" min="0" max="50"
              value={selectedTable.minCovers || 0}
              onChange={e => handleChange('minCovers', parseInt(e.target.value, 10) || 0)}
              onBlur={saveSelectedTable}
            />
          </div>
          <div className="tcm-prop-group">
            <label>Max Covers</label>
            <input
              type="number" min="1" max="50"
              value={selectedTable.maxCovers || selectedTable.capacity.maximum}
              onChange={e => handleChange('maxCovers', parseInt(e.target.value, 10) || 1)}
              onBlur={saveSelectedTable}
            />
          </div>
        </div>

        <div className="tcm-prop-group">
          <label>Area</label>
          <select
            value={selectedTable.areaId || ''}
            onChange={e => { handleChange('areaId', e.target.value || undefined); saveSelectedTable(); }}
          >
            <option value="">No Area</option>
            {areas.map(a => (
              <option key={a.areaId} value={a.areaId}>{a.name}</option>
            ))}
          </select>
        </div>

        <div className="tcm-prop-group">
          <label>Status</label>
          <select
            value={selectedTable.status}
            onChange={e => { pushUndo(); handleChange('status', e.target.value); }}
          >
            {ALL_STATUSES.map(s => (
              <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>
            ))}
          </select>
        </div>

        <div className="tcm-prop-group">
          <label>Rotation</label>
          <input
            type="number" min="0" max="360" step="15"
            value={selectedTable.rotation}
            onChange={e => handleChange('rotation', parseInt(e.target.value, 10) || 0)}
            onBlur={saveSelectedTable}
          />
        </div>

        <div className="tcm-prop-group">
          <label>Notes</label>
          <textarea
            value={selectedTable.notes || ''}
            onChange={e => handleChange('notes', e.target.value)}
            onBlur={saveSelectedTable}
            placeholder="Optional notes..."
            rows={2}
          />
        </div>

        <div className="tcm-prop-toggle">
          <input
            type="checkbox"
            checked={selectedTable.active}
            onChange={e => { handleChange('active', e.target.checked); saveSelectedTable(); }}
            id="tcm-active-toggle"
          />
          <label htmlFor="tcm-active-toggle">Active</label>
        </div>

        <div className="tcm-prop-actions">
          <button className="tcm-btn tcm-btn--primary tcm-btn--small" onClick={saveSelectedTable}>
            Save
          </button>
          <button
            className="tcm-btn tcm-btn--danger tcm-btn--small"
            onClick={() => deleteTable(selectedTable.tableId)}
          >
            Delete
          </button>
        </div>
      </div>
    );
  };

  // ─── Status Dropdown (Live Mode) ────────────────────────────────────────

  const renderStatusDropdown = () => {
    if (!statusDropdown) return null;
    return (
      <div
        className="tcm-status-dropdown"
        style={{ left: statusDropdown.x, top: statusDropdown.y }}
      >
        {ALL_STATUSES.map(s => (
          <button
            key={s}
            className="tcm-status-dropdown-item"
            onClick={() => changeStatus(statusDropdown.tableId, s)}
          >
            <div className="tcm-status-swatch" style={{ background: STATUS_CONFIG[s].border }} />
            {STATUS_CONFIG[s].label}
          </button>
        ))}
      </div>
    );
  };

  // ─── Context Menu ───────────────────────────────────────────────────────

  const renderContextMenu = () => {
    if (!contextMenu || mode !== 'edit') return null;
    const { x, y, tableId } = contextMenu;
    return (
      <div className="tcm-context-menu" style={{ left: x, top: y }}>
        {tableId && (
          <>
            <button className="tcm-context-menu-item" onClick={() => duplicateTable(tableId)}>Duplicate</button>
            <button className="tcm-context-menu-item" onClick={() => { pushUndo(); deleteTable(tableId); setContextMenu(null); }}>Delete</button>
            <div className="tcm-context-menu-divider" />
            <button className="tcm-context-menu-item" onClick={() => rotateTable(tableId, 90)}>Rotate 90 CW</button>
            <button className="tcm-context-menu-item" onClick={() => rotateTable(tableId, -90)}>Rotate 90 CCW</button>
            <div className="tcm-context-menu-divider" />
            <button className="tcm-context-menu-item" onClick={() => {
              const table = tables.find(t => t.tableId === tableId);
              if (table) { setModalTable({ ...table }); setShowPropertiesModal(true); }
              setContextMenu(null);
            }}>Edit Properties</button>
          </>
        )}
        {!tableId && selectedFixtureId && (
          <>
            <button className="tcm-context-menu-item" onClick={() => { deleteFixture(selectedFixtureId); setSelectedFixtureId(null); setContextMenu(null); }}>Delete Fixture</button>
            <button className="tcm-context-menu-item" onClick={() => {
              setFixtures(prev => {
                const updated = prev.map(f => f.fixtureId === selectedFixtureId ? { ...f, rotation: (f.rotation + 90) % 360 } : f);
                const fix = updated.find(f => f.fixtureId === selectedFixtureId);
                if (fix) updateFixtureAPI(fix);
                return updated;
              });
              setContextMenu(null);
            }}>Rotate 90</button>
          </>
        )}
        {!tableId && selectedWallId && (
          <>
            <button className="tcm-context-menu-item" onClick={() => { deleteWall(selectedWallId); setSelectedWallId(null); setContextMenu(null); }}>Delete Wall</button>
          </>
        )}
        {!tableId && !selectedFixtureId && !selectedWallId && (
          <>
            <button className="tcm-context-menu-item" onClick={() => { setSelectedTableIds(new Set(tables.map(t => t.tableId))); setContextMenu(null); }}>Select All</button>
            <button className="tcm-context-menu-item" onClick={() => { zoomReset(); setContextMenu(null); }}>Reset View</button>
          </>
        )}
      </div>
    );
  };

  // ─── Minimap ──────────────────────────────────────────────────────────

  const renderMinimap = () => {
    if (!showMinimap) return null;
    const mmW = 180, mmH = 120;
    const scaleX = mmW / canvasSize.width;
    const scaleY = mmH / canvasSize.height;
    const mmScale = Math.min(scaleX, scaleY);

    // Viewport rectangle
    const wrapper = canvasWrapperRef.current;
    const vpW = wrapper ? wrapper.clientWidth / scale : canvasSize.width;
    const vpH = wrapper ? wrapper.clientHeight / scale : canvasSize.height;
    const vpX = -pan.x / scale;
    const vpY = -pan.y / scale;

    return (
      <div className="tcm-minimap" onClick={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const clickX = (e.clientX - rect.left) / mmScale;
        const clickY = (e.clientY - rect.top) / mmScale;
        setPan({ x: -(clickX - vpW / 2) * scale, y: -(clickY - vpH / 2) * scale });
      }}>
        <svg width={mmW} height={mmH}>
          <rect width={mmW} height={mmH} fill={bgColor} opacity={0.5} />
          {tables.map(t => {
            const section = getSectionForTable(t.tableId);
            return (
              <rect
                key={t.tableId}
                x={t.position.x * mmScale}
                y={t.position.y * mmScale}
                width={Math.max(3, t.dimensions.width * mmScale)}
                height={Math.max(3, t.dimensions.height * mmScale)}
                fill={section?.color || STATUS_CONFIG[t.status]?.border || '#6b7280'}
                rx={t.shape === 'circle' || t.shape === 'oval' ? 50 : 1}
              />
            );
          })}
          <rect
            x={Math.max(0, vpX * mmScale)}
            y={Math.max(0, vpY * mmScale)}
            width={Math.min(mmW, vpW * mmScale)}
            height={Math.min(mmH, vpH * mmScale)}
            fill="none"
            stroke="#3b82f6"
            strokeWidth={1.5}
          />
        </svg>
      </div>
    );
  };

  // ─── Section Summary Bar (Live Mode) ──────────────────────────────────

  const renderSectionSummaryBar = () => {
    if (mode !== 'live' || sections.length === 0) return null;
    return (
      <div className="tcm-section-summary-bar">
        {sections.map(section => {
          const sectionTables = tables.filter(t => section.tableIds.includes(t.tableId));
          const occupied = sectionTables.filter(t => t.status === 'occupied').length;
          const covers = sectionTables.reduce((sum, t) => sum + (t.partySize || 0), 0);
          return (
            <div key={section.sectionId} className="tcm-section-summary-item" style={{ borderLeftColor: section.color }}>
              <span className="tcm-section-summary-name">{section.name}</span>
              {section.serverName && <span className="tcm-section-summary-server">{section.serverName}</span>}
              <span className="tcm-section-summary-stat">{occupied}/{sectionTables.length} tables</span>
              <span className="tcm-section-summary-stat">{covers} covers</span>
            </div>
          );
        })}
      </div>
    );
  };

  // ─── Alignment Guides ─────────────────────────────────────────────────

  const renderAlignGuides = () => {
    if (alignGuides.length === 0) return null;
    return alignGuides.map((g, i) => (
      <div
        key={i}
        className={`tcm-align-guide tcm-align-guide--${g.type}`}
        style={g.type === 'h' ? { top: g.pos } : { left: g.pos }}
      />
    ));
  };

  // ─── Selection Rectangle ──────────────────────────────────────────────

  const renderSelectionRect = () => {
    if (!selectionRect || !isDrawingSelection) return null;
    const left = Math.min(selectionRect.startX, selectionRect.endX);
    const top = Math.min(selectionRect.startY, selectionRect.endY);
    const width = Math.abs(selectionRect.endX - selectionRect.startX);
    const height = Math.abs(selectionRect.endY - selectionRect.startY);
    return (
      <div className="tcm-selection-rect" style={{ left, top, width, height }} />
    );
  };

  // ─── Background Pattern SVG ───────────────────────────────────────────

  const renderBgPattern = () => {
    if (!bgPattern) return null;
    let patternContent: React.ReactNode = null;
    if (bgPattern === 'hardwood') {
      patternContent = (
        <pattern id="tcm-bg-pattern" width="40" height="120" patternUnits="userSpaceOnUse">
          <rect width="40" height="120" fill={bgColor} />
          <rect x="0" y="0" width="40" height="29" fill="rgba(0,0,0,0.03)" stroke="rgba(0,0,0,0.08)" strokeWidth="0.5" />
          <rect x="0" y="30" width="40" height="29" fill="rgba(0,0,0,0.05)" stroke="rgba(0,0,0,0.08)" strokeWidth="0.5" />
          <rect x="0" y="60" width="40" height="29" fill="rgba(0,0,0,0.02)" stroke="rgba(0,0,0,0.08)" strokeWidth="0.5" />
          <rect x="0" y="90" width="40" height="29" fill="rgba(0,0,0,0.04)" stroke="rgba(0,0,0,0.08)" strokeWidth="0.5" />
        </pattern>
      );
    } else if (bgPattern === 'tile') {
      patternContent = (
        <pattern id="tcm-bg-pattern" width="40" height="40" patternUnits="userSpaceOnUse">
          <rect width="40" height="40" fill={bgColor} />
          <rect x="0" y="0" width="19" height="19" fill="rgba(0,0,0,0.03)" stroke="rgba(0,0,0,0.1)" strokeWidth="0.5" />
          <rect x="20" y="20" width="19" height="19" fill="rgba(0,0,0,0.03)" stroke="rgba(0,0,0,0.1)" strokeWidth="0.5" />
        </pattern>
      );
    } else if (bgPattern === 'carpet') {
      patternContent = (
        <pattern id="tcm-bg-pattern" width="8" height="8" patternUnits="userSpaceOnUse">
          <rect width="8" height="8" fill={bgColor} />
          <circle cx="2" cy="2" r="0.5" fill="rgba(0,0,0,0.06)" />
          <circle cx="6" cy="6" r="0.5" fill="rgba(0,0,0,0.06)" />
        </pattern>
      );
    } else if (bgPattern === 'concrete') {
      patternContent = (
        <pattern id="tcm-bg-pattern" width="100" height="100" patternUnits="userSpaceOnUse">
          <rect width="100" height="100" fill={bgColor} />
          <circle cx="20" cy="30" r="1" fill="rgba(0,0,0,0.04)" />
          <circle cx="70" cy="15" r="1.5" fill="rgba(0,0,0,0.03)" />
          <circle cx="50" cy="80" r="1" fill="rgba(0,0,0,0.04)" />
        </pattern>
      );
    }
    return (
      <svg className="tcm-bg-pattern-layer" width={canvasSize.width} height={canvasSize.height}>
        <defs>{patternContent}</defs>
        <rect width="100%" height="100%" fill="url(#tcm-bg-pattern)" />
      </svg>
    );
  };

  // ─── Table Properties Modal (double-click) ──────────────────────────────

  const renderPropertiesModal = () => {
    if (!showPropertiesModal || !modalTable) return null;

    const handleModalChange = (field: string, value: any) => {
      setModalTable(prev => prev ? { ...prev, [field]: value } : null);
    };

    return (
      <div className="tcm-modal-overlay" onClick={() => { setShowPropertiesModal(false); setModalTable(null); }}>
        <div className="tcm-modal" onClick={e => e.stopPropagation()}>
          <h3>Table Properties - #{modalTable.tableNumber}</h3>

          <div className="tcm-modal-row">
            <div className="tcm-prop-group">
              <label>Table Number</label>
              <input value={modalTable.tableNumber} onChange={e => handleModalChange('tableNumber', e.target.value)} />
            </div>
            <div className="tcm-prop-group">
              <label>Display Name</label>
              <input value={modalTable.displayName || ''} onChange={e => handleModalChange('displayName', e.target.value)} placeholder="Optional" />
            </div>
          </div>

          <div className="tcm-modal-row">
            <div className="tcm-prop-group">
              <label>Shape</label>
              <select value={modalTable.shape} onChange={e => handleModalChange('shape', e.target.value)}>
                <option value="square">Square</option>
                <option value="circle">Round</option>
                <option value="rectangle">Rectangle</option>
                <option value="oval">Oval</option>
                <option value="booth">Booth</option>
                <option value="bar">Bar</option>
              </select>
            </div>
            <div className="tcm-prop-group">
              <label>Status</label>
              <select value={modalTable.status} onChange={e => handleModalChange('status', e.target.value)}>
                {ALL_STATUSES.map(s => <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>)}
              </select>
            </div>
          </div>

          <div className="tcm-modal-row">
            <div className="tcm-prop-group">
              <label>Default Seats</label>
              <input type="number" min="1" value={modalTable.capacity.default}
                onChange={e => handleModalChange('capacity', { ...modalTable.capacity, default: parseInt(e.target.value, 10) || 1 })} />
            </div>
            <div className="tcm-prop-group">
              <label>Max Seats</label>
              <input type="number" min="1" value={modalTable.capacity.maximum}
                onChange={e => handleModalChange('capacity', { ...modalTable.capacity, maximum: parseInt(e.target.value, 10) || 1 })} />
            </div>
          </div>

          <div className="tcm-modal-row">
            <div className="tcm-prop-group">
              <label>Min Covers</label>
              <input type="number" min="0" value={modalTable.minCovers || 0}
                onChange={e => handleModalChange('minCovers', parseInt(e.target.value, 10) || 0)} />
            </div>
            <div className="tcm-prop-group">
              <label>Max Covers</label>
              <input type="number" min="1" value={modalTable.maxCovers || modalTable.capacity.maximum}
                onChange={e => handleModalChange('maxCovers', parseInt(e.target.value, 10) || 1)} />
            </div>
          </div>

          <div className="tcm-modal-row">
            <div className="tcm-prop-group">
              <label>Area</label>
              <select value={modalTable.areaId || ''} onChange={e => handleModalChange('areaId', e.target.value || undefined)}>
                <option value="">No Area</option>
                {areas.map(a => <option key={a.areaId} value={a.areaId}>{a.name}</option>)}
              </select>
            </div>
            <div className="tcm-prop-group">
              <label>Revenue Center</label>
              <input value={modalTable.revenueCenter || ''} onChange={e => handleModalChange('revenueCenter', e.target.value)} placeholder="e.g. Bar, Patio" />
            </div>
          </div>

          <div className="tcm-modal-row">
            <div className="tcm-prop-group">
              <label>Rotation</label>
              <input type="number" min="0" max="360" step="15" value={modalTable.rotation}
                onChange={e => handleModalChange('rotation', parseInt(e.target.value, 10) || 0)} />
            </div>
            <div className="tcm-prop-group">
              <label>Width</label>
              <input type="number" min="30" value={modalTable.dimensions.width}
                onChange={e => handleModalChange('dimensions', { ...modalTable.dimensions, width: parseInt(e.target.value, 10) || 60 })} />
            </div>
            <div className="tcm-prop-group">
              <label>Height</label>
              <input type="number" min="30" value={modalTable.dimensions.height}
                onChange={e => handleModalChange('dimensions', { ...modalTable.dimensions, height: parseInt(e.target.value, 10) || 60 })} />
            </div>
          </div>

          <div className="tcm-prop-group">
            <label>Tags</label>
            <div className="tcm-tags">
              {(modalTable.tags || []).map((tag, i) => (
                <span key={i} className="tcm-tag">
                  {tag}
                  <span className="tcm-tag-remove" onClick={() => {
                    handleModalChange('tags', (modalTable.tags || []).filter((_, idx) => idx !== i));
                  }}>x</span>
                </span>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
              <input
                value={newTag}
                onChange={e => setNewTag(e.target.value)}
                placeholder="Add tag..."
                onKeyDown={e => {
                  if (e.key === 'Enter' && newTag.trim()) {
                    handleModalChange('tags', [...(modalTable.tags || []), newTag.trim()]);
                    setNewTag('');
                  }
                }}
                style={{ flex: 1 }}
              />
              <button className="tcm-btn tcm-btn--secondary tcm-btn--small" onClick={() => {
                if (newTag.trim()) {
                  handleModalChange('tags', [...(modalTable.tags || []), newTag.trim()]);
                  setNewTag('');
                }
              }}>+</button>
            </div>
          </div>

          <div className="tcm-prop-group">
            <label>Notes</label>
            <textarea value={modalTable.notes || ''} onChange={e => handleModalChange('notes', e.target.value)} placeholder="Notes..." rows={3} />
          </div>

          <div className="tcm-prop-group">
            <label>Color Override</label>
            <div className="tcm-color-picker">
              <input type="color" value={modalTable.colorOverride || '#3b82f6'} onChange={e => handleModalChange('colorOverride', e.target.value)} />
              {modalTable.colorOverride && (
                <span className="tcm-color-clear" onClick={() => handleModalChange('colorOverride', undefined)}>Clear</span>
              )}
            </div>
          </div>

          <div className="tcm-prop-toggle">
            <input type="checkbox" checked={modalTable.active} onChange={e => handleModalChange('active', e.target.checked)} id="tcm-modal-active" />
            <label htmlFor="tcm-modal-active">Active</label>
          </div>

          <div className="tcm-modal-actions">
            <button className="tcm-btn tcm-btn--secondary" onClick={() => { setShowPropertiesModal(false); setModalTable(null); }}>Cancel</button>
            <button className="tcm-btn tcm-btn--primary" onClick={saveModalTable}>Save Changes</button>
          </div>
        </div>
      </div>
    );
  };

  // ─── Stats Bar (Live Mode) ──────────────────────────────────────────────

  const renderStatsBar = () => {
    if (mode !== 'live') return null;
    return (
      <div className="tcm-stats-bar">
        <div className="tcm-stats-bar-item">
          <span className="tcm-stats-bar-label">Tables:</span>
          <span className="tcm-stats-bar-value">{tables.length}</span>
        </div>
        <div className="tcm-stats-bar-item">
          <span className="tcm-stats-bar-label">Covers:</span>
          <span className="tcm-stats-bar-value">{totalCovers} / {totalCapacity}</span>
        </div>
        <div className="tcm-stats-bar-item">
          <span className="tcm-stats-bar-label">Available:</span>
          <span className="tcm-stats-bar-value">{statusCounts.available}</span>
        </div>
        <div className="tcm-stats-bar-item">
          <span className="tcm-stats-bar-label">Occupied:</span>
          <span className="tcm-stats-bar-value">{statusCounts.occupied}</span>
        </div>
        <div className="tcm-stats-bar-item">
          <span className="tcm-stats-bar-label">Reserved:</span>
          <span className="tcm-stats-bar-value">{statusCounts.reserved}</span>
        </div>
        <div className="tcm-stats-bar-item">
          <span className="tcm-stats-bar-label">Dirty:</span>
          <span className="tcm-stats-bar-value">{dirtyCount}</span>
        </div>
      </div>
    );
  };

  // ─── Drag Ghost ─────────────────────────────────────────────────────────

  const renderDragGhost = () => {
    if (!isDraggingFromLibrary || !dragLibraryItem) return null;
    return (
      <div
        className="tcm-drag-ghost"
        style={{
          left: dragGhostPos.x - dragLibraryItem.width / 2,
          top: dragGhostPos.y - dragLibraryItem.height / 2,
          width: dragLibraryItem.width,
          height: dragLibraryItem.height,
        }}
      >
        {dragLibraryItem.displayName}
      </div>
    );
  };

  // ─── Main Render ────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="tcm-container tcm-loading">
        <span className="tcm-loading-spinner">Loading floor plan...</span>
      </div>
    );
  }

  return (
    <div className="tcm-container">
      {/* Error */}
      {error && (
        <div className="tcm-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>x</button>
        </div>
      )}

      {/* Header */}
      <div className="tcm-header">
        <div className="tcm-header-left">
          <h2>Floor Plan</h2>
          <div className="tcm-layout-selector">
            <label style={{ color: '#a0a0b8', fontSize: '0.8rem', marginRight: 4 }}>Space:</label>
            <select
              value={currentLayout?.layoutId || ''}
              onChange={e => {
                if (e.target.value === '__add_space__') {
                  const name = window.prompt('Enter space name (e.g. Patio, Upstairs, Bar):');
                  if (name && name.trim()) {
                    createSpaceAPI(name.trim()).then(created => {
                      if (created) {
                        setLayouts(prev => [...prev, created]);
                        setCurrentLayout(created);
                        setCanvasSize(created.canvasSize);
                      }
                    });
                  }
                  return;
                }
                const layout = layouts.find(l => l.layoutId === e.target.value);
                if (layout) {
                  setCurrentLayout(layout);
                  setCanvasSize(layout.canvasSize);
                }
              }}
            >
              {layouts.map(l => (
                <option key={l.layoutId} value={l.layoutId}>{l.name}</option>
              ))}
              <option value="__add_space__">+ Add Space</option>
            </select>
          </div>
          <button
            className={`tcm-grid-toggle ${gridMode !== 'hidden' ? 'active' : ''}`}
            onClick={() => setGridMode(prev => prev === 'visible' ? 'snap' : prev === 'snap' ? 'hidden' : 'visible')}
            title={`Grid: ${gridMode}`}
          >
            Grid: {gridMode === 'visible' ? 'On' : gridMode === 'snap' ? 'Snap' : 'Off'}
          </button>
        </div>
        <div className="tcm-header-actions">
          {mode === 'edit' && (
            <>
              <button className="tcm-btn--icon tcm-btn" onClick={undo} disabled={undoStack.length === 0} title="Undo (Ctrl+Z)">
                &#x21A9;
              </button>
              <button className="tcm-btn--icon tcm-btn" onClick={redo} disabled={redoStack.length === 0} title="Redo (Ctrl+Y)">
                &#x21AA;
              </button>
            </>
          )}
          <button
            className={`tcm-btn ${isDirty ? 'tcm-btn--save-dirty' : 'tcm-btn--save-clean'}`}
            onClick={saveAll}
            disabled={isSaving || !isDirty}
          >
            {isSaving ? 'Saving...' : isDirty ? 'Save' : 'Saved'}
          </button>
          {lastSaved && !isDirty && (
            <span className="tcm-save-timestamp">Last saved {lastSaved}</span>
          )}
          <div className="tcm-mode-toggle">
            <button className={mode === 'edit' ? 'active' : ''} onClick={() => { setMode('edit'); setStatusDropdown(null); }}>
              Edit
            </button>
            <button className={mode === 'live' ? 'active' : ''} onClick={() => { setMode('live'); setSelectedTableId(null); setSelectedTableIds(new Set()); }}>
              Live
            </button>
            <button className={mode === 'section' ? 'active' : ''} onClick={() => { setMode('section'); setSelectedTableId(null); setSelectedTableIds(new Set()); }}>
              Section
            </button>
          </div>
          <div className="tcm-zoom-controls">
            <button onClick={zoomOut} title="Zoom out">-</button>
            <span className="tcm-zoom-label">{Math.round(scale * 100)}%</span>
            <button onClick={zoomIn} title="Zoom in">+</button>
            <button onClick={zoomReset} title="Reset zoom" style={{ fontSize: '11px' }}>Fit</button>
          </div>
          {onClose && (
            <button className="tcm-btn tcm-btn--secondary tcm-btn--small" onClick={onClose}>Close</button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="tcm-content">
        {/* Left Sidebar */}
        {mode === 'edit' && renderEditSidebar()}
        {mode === 'live' && renderLiveSidebar()}
        {mode === 'section' && renderSectionSidebar()}

        {/* Canvas */}
        <div
          ref={canvasWrapperRef}
          className={`tcm-canvas-wrapper ${showDropIndicator ? 'dragging-over' : ''} ${editTool === 'wall' && mode === 'edit' ? 'tcm-wall-cursor' : ''}`}
          onMouseDown={handleCanvasMouseDown}
          onMouseMove={handleCanvasMouseMove}
          onMouseUp={handleCanvasMouseUp}
          onMouseLeave={handleCanvasMouseUp}
          onWheel={handleWheel}
          onContextMenu={handleCanvasContextMenu}
          onDoubleClick={handleCanvasDoubleClick}
          onDragOver={mode === 'edit' ? (e: React.DragEvent) => {
            e.preventDefault();
            const fixtureType = e.dataTransfer.types.includes('fixture-type');
            if (fixtureType || dragLibraryItem) {
              e.dataTransfer.dropEffect = 'copy';
              setShowDropIndicator(true);
              setDragGhostPos({ x: e.clientX, y: e.clientY });
            }
          } : undefined}
          onDragLeave={mode === 'edit' ? handleCanvasDragLeave : undefined}
          onDrop={mode === 'edit' ? (e: React.DragEvent) => {
            e.preventDefault();
            setShowDropIndicator(false);
            const fixtureType = e.dataTransfer.getData('fixture-type') as FixtureType;
            if (fixtureType) {
              const coords = getCanvasCoords(e.clientX, e.clientY);
              addFixture(fixtureType, coords.x - 30, coords.y - 20);
              return;
            }
            handleCanvasDrop(e);
          } : undefined}
          onClick={() => { setStatusDropdown(null); setContextMenu(null); }}
        >
          <div
            className="tcm-canvas"
            style={{
              width: canvasSize.width,
              height: canvasSize.height,
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
              background: bgColor,
            }}
          >
            {renderBgPattern()}
            {renderGrid()}
            {renderWalls()}
            {renderMergeConnectors()}
            {renderFixtures()}
            {showDropIndicator && <div className="tcm-drop-indicator">Drop here</div>}
            {filteredTables.map(renderTable)}
            {renderAlignGuides()}
            {renderSelectionRect()}
          </div>
          {renderStatusDropdown()}
          {renderContextMenu()}
          {renderMinimap()}
        </div>

        {/* Right Properties Panel */}
        {renderPropertiesPanel()}
      </div>

      {/* Stats Bar */}
      {renderStatsBar()}
      {renderSectionSummaryBar()}

      {/* Modal */}
      {renderPropertiesModal()}

      {/* Drag Ghost */}
      {renderDragGhost()}
    </div>
  );
};

export default TableChartManager;
